import React, { useState, useEffect, useMemo, useRef } from 'react';
import { fgForwardGeocode, fgReverseGeocode, fgFootball } from './lib/fgApi';
import { 
  fgFootballCompetitions, 
  fgFootballCountries, 
  fgFootballTeams,
  fgFootballStandings,
  fgFootballMatches
} from './lib/fgApi';
import AboutData from './AboutData';

// 🔥 HELPER FUNCTION
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Builds the outbound travel links for a stadium's city/country. Centralized so
// swapping to affiliate providers later only touches this one function.
const buildTravelLinks = (city, country) => {
  const q = encodeURIComponent(`${city}, ${country}`);
  return {
    flights: `https://www.google.com/travel/flights?q=Flights%20to%20${q}`,
    hotels:  `https://www.google.com/travel/search?q=${q}`
  };
};

// Same shape/size/anchor as the default stadium marker icon (inlined at marker
// creation) - amber instead of green, used to recolor markers for teams with a
// fixture in the next 7 days. Colour only; never touches shape, size, or position.
const buildMatchDayMarkerIcon = () => ({
  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg width="36" height="48" viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      <!-- Outer glow -->
      <circle cx="18" cy="16" r="14" fill="#f59e0b" opacity="0.3" filter="url(#glow)"/>

      <!-- Main pin shape -->
      <path d="M 18 2 C 10.268 2 4 8.268 4 16 C 4 24 18 44 18 44 C 18 44 32 24 32 16 C 32 8.268 25.732 2 18 2 Z"
            fill="url(#matchDayGradient)"
            stroke="#ffffff"
            stroke-width="2.5"
            filter="url(#glow)"/>

      <!-- Gradient definition -->
      <defs>
        <linearGradient id="matchDayGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:#f59e0b;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#b45309;stop-opacity:1" />
        </linearGradient>
      </defs>

      <!-- Inner stadium icon -->
      <g transform="translate(18, 16)">
        <!-- Stadium structure -->
        <ellipse cx="0" cy="0" rx="7" ry="5" fill="#ffffff" opacity="0.9"/>
        <ellipse cx="0" cy="0" rx="5" ry="3" fill="#f59e0b"/>

        <!-- Field lines -->
        <line x1="-5" y1="0" x2="5" y2="0" stroke="#ffffff" stroke-width="0.5"/>
        <circle cx="0" cy="0" r="1.5" fill="none" stroke="#ffffff" stroke-width="0.5"/>
      </g>
    </svg>
  `),
  scaledSize: new window.google.maps.Size(36, 48),
  anchor: new window.google.maps.Point(18, 44)
});

// ===== Normalize API keys into constants (robust against missing nested objects) =====
const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const FOOTBALL_KEY = 'PROXY_HANDLES_THIS';

const CACHE_CONFIG = {
  enabled: true,
  ttl: 6 * 60 * 60 * 1000,
  maxInitialCountries: 15,
  priorityCountries: ['GB', 'US', 'DE', 'FR', 'ES', 'IT', 'BR', 'AR', 'NL', 'PT', 'BE', 'CH', 'SE', 'NO', 'DK'],
  enableProgressiveLoading: true
};

// Map Google's 2-letter codes to Football-Data.org's 3-letter codes
const GOOGLE_TO_FOOTBALL_CODE = {
  'GB': 'ENG',  // Google's GB → Football's ENG
  'DE': 'DEU',  // Germany
  'FR': 'FRA',  // France
  'ES': 'ESP',  // Spain
  'IT': 'ITA',  // Italy
  'BR': 'BRA',  // Brazil
  'AR': 'ARG',  // Argentina
  'NL': 'NLD',  // Netherlands
  'PT': 'POR',  // Portugal
  'BE': 'BEL',  // Belgium
};

// Helper to convert Google code to Football code
const googleToFootballCode = (googleCode) => {
  return GOOGLE_TO_FOOTBALL_CODE[googleCode] || googleCode;
};

// ===== Country boundary source =====
// Subunits, not admin_0_countries: England/Scotland/Wales/Northern-Ireland are
// separate premium keys and only the subunit layer separates them.
//
// Pinned vendored copy in public/, downloaded 2026-09-07 from master of
// nvkelso/natural-earth-vector (geojson/ne_50m_admin_0_map_subunits.geojson).
// It is served from our own origin rather than raw.githubusercontent.com
// because that host rate-limits by IP and serves a moving branch: an upstream
// commit could redraw these borders with no change on this side.
const NE_GEOJSON_URL = '/ne_50m_admin_0_map_subunits.geojson';

// Single in-flight/parsed promise. Both the polygon render and every hover
// highlight share it, so the ~3MB payload is fetched and JSON-parsed once.
let neGeoJsonPromise = null;
const loadCountryGeoJson = () => {
  if (!neGeoJsonPromise) {
    neGeoJsonPromise = fetch(NE_GEOJSON_URL)
      .then(res => {
        if (!res.ok) throw new Error(`GeoJSON HTTP ${res.status}`);
        return res.json();
      })
      .catch(err => {
        neGeoJsonPromise = null; // a transient failure must not poison the session
        throw err;
      });
  }
  return neGeoJsonPromise;
};

// lowercase, [-_] to space, strip non-letters, collapse space, trim
const normaliseCountry = (s) =>
  String(s == null ? '' : s)
    .toLowerCase()
    .replace(/[-_]/g, ' ')
    .replace(/[^a-z\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const COUNTRY_ALIASES = {
  'usa': 'united states of america',
  'bosnia': 'bosnia and herzegovina',
  'macedonia': 'north macedonia'
};

const applyCountryAlias = (n) => COUNTRY_ALIASES[n] || n;

// byName keys on NAME_EN and NAME; byAdmin keys on ADMIN. Values are Sets of
// feature INDEXES so a feature is never collected twice for one country.
const buildFeatureIndexes = (geoJson) => {
  const byName = new Map();
  const byAdmin = new Map();
  const add = (index, key, i) => {
    if (!key) return;
    if (!index.has(key)) index.set(key, new Set());
    index.get(key).add(i);
  };
  geoJson.features.forEach((f, i) => {
    const p = f.properties || {};
    add(byName, normaliseCountry(p.NAME_EN), i);
    add(byName, normaliseCountry(p.NAME), i);
    add(byAdmin, normaliseCountry(p.ADMIN), i);
  });
  return { byName, byAdmin };
};

const resolveCountryFeatures = (countryKey, byName, byAdmin) => {
  const lookup = applyCountryAlias(normaliseCountry(countryKey));
  const hit = byName.get(lookup) || byAdmin.get(lookup);
  return hit && hit.size > 0 ? [...hit] : [];
};

// ===== NEW: Translate country NAME to code =====
const translateCountryNameToCode = (countryNameOrCode) => {
  // If it's already a 3-letter code, return it as-is
  if (countryNameOrCode && countryNameOrCode.length === 3 && countryNameOrCode === countryNameOrCode.toUpperCase()) {
    console.log(`🔍 Already a code: ${countryNameOrCode}`);
    return countryNameOrCode;
  }
  
  // Otherwise, translate name to code
  const nameToCode = {
    'England': 'ENG',
    'Germany': 'DEU',
    'France': 'FRA',
    'Spain': 'ESP',
    'Italy': 'ITA',
    'Brazil': 'BRA',
    'Netherlands': 'NLD',
    'Portugal': 'POR',
    'Belgium': 'BEL',
    'Argentina': 'ARG',
    'Europe': 'EUR'
  };
  
  const code = nameToCode[countryNameOrCode];
  console.log(`🔍 Translated: ${countryNameOrCode} → ${code || 'NOT FOUND'}`);
  return code || null;
};

// Compatibility shim - uses environment variables but provides API_CONFIG structure
const API_CONFIG = {
  football: {
    baseUrl: 'https://v3.football.api-sports.io',
    key: FOOTBALL_KEY, // This is just for compatibility, not used
    endpoints: {
      countries: '/countries',
      leagues: '/leagues',
      venues: '/venues'
    }
  },
  googleMaps: {
    key: GOOGLE_KEY
  },
  cache: CACHE_CONFIG
};

if (!GOOGLE_KEY) console.warn('Google key missing: GOOGLE_KEY is empty.');
if (!FOOTBALL_KEY) console.warn('Football key missing: FOOTBALL_KEY is empty.');


// --- Google Maps loader: ONE script tag for the whole app ---
// A second tag with different params re-registers google.maps, which swaps the
// class identities out from under any object already built from the first one.
// That is what produced 133x "setMap: not an instance of Map": the Map came
// from loader A, the Polygons (built after an await) from loader B.
//
// Library set is deliberately minimal. `places` was requested by both old
// loaders and is used nowhere. `marker` is not needed either: the two
// `new google.maps.Marker` calls are the legacy core Marker, and no mapId is
// set, so AdvancedMarkerElement is not in play. Geocoding goes through the
// server proxy in lib/fgApi.js, not google.maps.Geocoder.
let googleMapsPromise = null;

function loadGoogleMapsOnce() {
  if (googleMapsPromise) return googleMapsPromise;

  // Already usable (warm reload, or a tag from a previous mount finished).
  if (typeof window.google?.maps?.Map === 'function') {
    googleMapsPromise = Promise.resolve();
    return googleMapsPromise;
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    if (!GOOGLE_KEY) {
      reject(new Error('Google Maps key missing: GOOGLE_KEY is empty.'));
      return;
    }

    const finish = async () => {
      // "Tag loaded" is NOT "API usable": with loading=async the bootstrap has
      // not even attached importLibrary by the time onload fires, so testing it
      // once and skipping the await left us with no Map constructor. Poll for
      // whichever shows up first - a populated legacy namespace, or the
      // bootstrap's importLibrary - instead of giving up on the first miss.
      const POLL_MS = 50;
      const TIMEOUT_MS = 10000;
      try {
        const deadline = Date.now() + TIMEOUT_MS;
        while (typeof window.google?.maps?.Map !== 'function') {
          if (typeof window.google?.maps?.importLibrary === 'function') {
            await window.google.maps.importLibrary('maps');
            break; // fall through to the Map re-check below
          }
          if (Date.now() >= deadline) break;
          await new Promise((r) => setTimeout(r, POLL_MS));
        }
        if (typeof window.google?.maps?.Map !== 'function') {
          // Name what was actually missing so the next failure is diagnosable.
          const missing = typeof window.google?.maps?.importLibrary === 'function'
            ? 'window.google.maps.importLibrary was present but importLibrary("maps") did not produce window.google.maps.Map'
            : 'neither window.google.maps.Map nor window.google.maps.importLibrary ever appeared';
          throw new Error(
            `Google Maps unusable after ${TIMEOUT_MS}ms: ${missing}.`
          );
        }
        resolve();
      } catch (err) {
        googleMapsPromise = null; // allow a retry
        reject(err);
      }
    };

    const existing = document.querySelector('script[data-google-maps-loader]');
    if (existing) {
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error',
        () => { googleMapsPromise = null; reject(new Error('Google Maps script failed to load.')); },
        { once: true });
      return;
    }

    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_KEY}` +
            `&v=weekly&libraries=maps&language=en&loading=async`;
    s.async = true;
    s.defer = true;
    s.setAttribute('data-google-maps-loader', '1');
    s.onload = finish;
    s.onerror = () => {
      googleMapsPromise = null;
      reject(new Error('Google Maps script failed to load.'));
    };
    document.head.appendChild(s);
  });

  return googleMapsPromise;
}



// FootballGlobe - Professional API-Driven Implementation
// NO HARDCODING - All data from APIs following our architecture
const FootballGlobe = () => {
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  // Google's readiness as explicit state. window.google is a plain global, so
  // reading it inside an effect gives React nothing to re-run on: the init
  // effect fired twice before the script resolved and then had no dependency
  // left to change, hanging the map forever.
  const [googleMapsReady, setGoogleMapsReady] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  // Mobile-only slide-over drawer state for the country sidebar (desktop ignores this - the
  // sidebar is a permanent pane there). Starts closed each time a new country is selected.
  const [isSidebarDrawerOpen, setIsSidebarDrawerOpen] = useState(false);
  // Mobile-only stadium card: on viewports under 768px, tapping a stadium pin renders our own
  // bottom-sheet overlay instead of Google's InfoWindow. Desktop keeps using the InfoWindow.
  const [mobileStadiumCard, setMobileStadiumCard] = useState(null);
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [countriesData, setCountriesData] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [totalCountries, setTotalCountries] = useState(0);
  const countriesDataRef = useRef([]);
    const apiCacheRef = useRef({
      countries: null,
      countryDetails: {},
      lastFetch: null,
      cacheTimestamp: null
    });
    // STADIUM-RELATED STATES:
    const [stadiumPins, setStadiumPins] = useState([]);
    const [standings, setStandings] = useState(null);
    const [isLoadingStadiums, setIsLoadingStadiums] = useState(false);
    const [selectedStadium, setSelectedStadium] = useState(null);
    const [stadiumsCache, setStadiumsCache] = useState({});
    // ===== CACHE SYSTEM =====
    const [cachedStadiums, setCachedStadiums] = useState(null);
    const [cachedStandings, setCachedStandings] = useState(null);
    const [cacheLoaded, setCacheLoaded] = useState(false);
    const [cacheLoadError, setCacheLoadError] = useState(null);

    // Reset the mobile drawer to closed whenever the selected country changes,
    // so re-selecting a country always starts with the drawer collapsed.
    useEffect(() => {
      setIsSidebarDrawerOpen(false);
    }, [selectedCountry?.code]);

    // ===== UNIFIED MARKER MANAGEMENT =====
    // This ensures ALL markers are cleared regardless of which function created them
    const clearAllStadiumMarkers = () => {
      // Clear primary marker array
      if (window.currentStadiumMarkers) {
        window.currentStadiumMarkers.forEach(marker => marker.setMap(null));
        window.currentStadiumMarkers = [];
      }
      
      // Clear legacy marker array (from country clicks)
      if (window.stadiumMarkers && Array.isArray(window.stadiumMarkers)) {
        window.stadiumMarkers.forEach(marker => marker.setMap(null));
        window.stadiumMarkers = [];
      }
      
      console.log('🧹 CLEARED: All stadium markers removed');
    };

    // JOURNEY SYSTEM - Core differentiator (API-driven, no hardcoding)
    const [currentJourney, setCurrentJourney] = useState(null);
    const [journeyProgress, setJourneyProgress] = useState(0);
    // Enhanced State Management for World/Country Modes
    const [appMode, setAppMode] = useState('world'); // 'world' | 'country' | 'stadium'
    const [zoomLevel, setZoomLevel] = useState(2);
    const [viewState, setViewState] = useState({
      mode: 'world',
      center: { lat: 20, lng: 0 },
      zoom: 2,
      country: null,
      stadium: null
    });
    const [selectedLeague, setSelectedLeague] = useState(null);
    const [availableLeagues, setAvailableLeagues] = useState([]);
    
    
    // Journey definitions - Will be populated from football API data
    const JOURNEY_TYPES = {
      worldCupWinners: {
        id: 'worldCupWinners',
        title: 'World Cup Champions Journey',
        description: 'Visit countries that have won the FIFA World Cup',
        countries: [], // Will be populated from API data
        icon: '🏆',
        difficulty: 'beginner',
        apiValidated: false
      },
      legendaryStadiums: {
        id: 'legendaryStadiums', 
        title: 'Legendary Stadiums Tour',
        description: 'Explore the most iconic football venues worldwide',
        countries: [], // Will be populated from stadium API data
        icon: '🏟️',
        difficulty: 'intermediate',
        apiValidated: false
      }
    };

    // Journey System - Streamlined for real usage (no validation waste)
    const initializeJourneys = () => {
      console.log('🎯 JOURNEY SYSTEM: Initialized for on-demand use');
      
      // Populate journeys with API data only when actually selected by user
      JOURNEY_TYPES.worldCupWinners.countries = ['BR', 'DE', 'IT', 'AR', 'FR', 'UY', 'ES', 'GB'];
      JOURNEY_TYPES.worldCupWinners.apiValidated = true;
      
      JOURNEY_TYPES.legendaryStadiums.countries = ['GB', 'ES', 'DE', 'IT', 'FR', 'BR', 'AR', 'NL'];
      JOURNEY_TYPES.legendaryStadiums.apiValidated = true;
      
      console.log('🏆 JOURNEYS: Ready for user selection');
    };
    
    // Initialize core systems when countries are loaded
    useEffect(() => {
      if (countriesData.length > 0 && API_CONFIG.football.key) {
        // Initialize journeys without API waste
        initializeJourneys();
        
        // BUILD TRANSLATION TABLE - Keep this
        buildCountryTranslationTable();
      }
    }, [countriesData]);
    // ===== LOAD CACHE FILES ON STARTUP =====
    useEffect(() => {
      async function loadCacheFiles() {
        console.log('⚡ WEEK 3: Loading cached data...');
        setCacheLoaded(false);
        
        try {
          const [stadiumsRes, standingsRes] = await Promise.all([
            fetch('/stadiums-premium.json'),
            fetch('/standings-premium-cache.json')
          ]);

          if (!stadiumsRes.ok || !standingsRes.ok) {
            throw new Error('Failed to fetch cache files');
          }

          const stadiumsData = await stadiumsRes.json();
          const standingsData = await standingsRes.json();

          setCachedStadiums(stadiumsData);
          setCachedStandings(standingsData);
          setCacheLoaded(true);

          console.log('✅ CACHE LOADED:', {
            stadiums: stadiumsData.totalStadiums || 'N/A',
            countries: Object.keys(stadiumsData.countries || {}).length,
            leagues: standingsData.totalLeagues || 'N/A',
            lastUpdated: stadiumsData.lastUpdated
          });

        } catch (error) {
          console.error('❌ CACHE LOAD FAILED:', error);
          setCacheLoadError(error.message);
          setCacheLoaded(true); // Still mark as loaded to avoid infinite loading
        }
      }

      loadCacheFiles();
    }, []); // Run once on mount
    
    // API CONTROL SYSTEM - Critical for cost management
    const API_CONTROLLER = {
      // Rate limits per API type
      limits: {
        football: {
          requestsPerMinute: 10,
          requestsPerHour: 100,
          requestsPerDay: 500
        },
        geocoding: {
          requestsPerMinute: 50,
          requestsPerHour: 2000,
          requestsPerDay: 5000
        }
      },
      
      // Track usage
      usage: {
        football: { minute: 0, hour: 0, day: 0, lastReset: { minute: 0, hour: 0, day: 0 } },
        geocoding: { minute: 0, hour: 0, day: 0, lastReset: { minute: 0, hour: 0, day: 0 } }
      },
      
      // Request queue for controlled execution
      queues: {
        football: [],
        geocoding: []
      },
      
      // Cache to avoid duplicate requests
      cache: new Map(),
      
      // Check if request is allowed
      canMakeRequest: function(apiType) {
        this.resetCountersIfNeeded(apiType);
        const limits = this.limits[apiType];
        const usage = this.usage[apiType];
        
        return usage.minute < limits.requestsPerMinute && 
              usage.hour < limits.requestsPerHour && 
              usage.day < limits.requestsPerDay;
      },
      
      // Reset counters when time windows expire
      resetCountersIfNeeded: function(apiType) {
        const now = Date.now();
        const usage = this.usage[apiType];
        
        // Reset minute counter
        if (now - usage.lastReset.minute >= 60000) {
          usage.minute = 0;
          usage.lastReset.minute = now;
        }
        
        // Reset hour counter
        if (now - usage.lastReset.hour >= 3600000) {
          usage.hour = 0;
          usage.lastReset.hour = now;
        }
        
        // Reset day counter
        if (now - usage.lastReset.day >= 86400000) {
          usage.day = 0;
          usage.lastReset.day = now;
        }
      },
      
      // Record API usage
      recordRequest: function(apiType) {
        this.usage[apiType].minute++;
        this.usage[apiType].hour++;
        this.usage[apiType].day++;
      },
      
      // Generate cache key
      getCacheKey: function(url, params = {}) {
        return `${url}_${JSON.stringify(params)}`;
      }
    };

    // ===== COUNTRY CODE TO NAME MAPPING =====
    const COUNTRY_CODE_TO_NAME = {
      'ENG': 'England',
      'ESP': 'Spain', 
      'DEU': 'Germany',
      'FRA': 'France',
      'ITA': 'Italy',
      'BRA': 'Brazil',
      'NLD': 'Netherlands',
      'POR': 'Portugal',
      'EUR': 'Europe'
    };
    // ===== CACHE HELPER FUNCTIONS =====
    
    /**
     * Get stadiums for a specific country from cache
     * @param {string} countryName - Country name (e.g., "England", "Spain")
     * @returns {Array} Array of stadium objects with GPS coordinates
     */
    const getStadiumsFromCache = (countryNameOrCode) => {
      if (!cachedStadiums || !cachedStadiums.countries) {
        console.warn('⚠️ Cache not loaded yet');
        return [];
      }

      // Convert country code to name if needed (ENG → England)
      const countryName = COUNTRY_CODE_TO_NAME[countryNameOrCode] || countryNameOrCode;
      console.log(`🔍 Looking up: "${countryNameOrCode}" → "${countryName}"`);

      const country = cachedStadiums.countries[countryName];
      if (!country) {
        console.warn(`⚠️ Country "${countryName}" not found in cache`);
        return [];
      }

      // Flatten all stadiums from all leagues in this country
      const stadiums = country.leagues.flatMap(league => 
        league.stadiums.map(stadium => ({
          ...stadium,
          leagueName: league.name,
          leagueId: league.id,
          country: countryName
        }))
      );

      console.log(`⚡ Loaded ${stadiums.length} stadiums for ${countryName} from cache`);
      return stadiums;
    };

    /**
     * Get standings for a specific league from cache
     * @param {number} leagueId - League ID (e.g., 39 for Premier League)
     * @returns {Object|null} League standings object or null
     */
    const getStandingsFromCache = (leagueId) => {
      if (!cachedStandings || !cachedStandings.leagues) {
        console.warn('⚠️ Standings cache not loaded yet');
        return null;
      }

      const standings = cachedStandings.leagues[leagueId];
      if (!standings) {
        console.warn(`⚠️ League ${leagueId} not found in cache`);
        return null;
      }

      console.log(`⚡ Loaded standings for ${standings.name} from cache`);
      return standings;
    };

    /**
     * Get all available countries from cache
     * @returns {Array} Array of country names
     */
    const getAvailableCountries = () => {
      if (!cachedStadiums || !cachedStadiums.countries) {
        return [];
      }
      return Object.keys(cachedStadiums.countries);
    };

    // EMERGENCY BRAKE - Stop all API calls if limits exceeded
    const emergencyBrake = {
      activated: false,
      
      activate: function(reason) {
        this.activated = true;
        console.error(`🚨 EMERGENCY BRAKE ACTIVATED: ${reason}`);
        // Clear all queues
        API_CONTROLLER.queues.football = [];
        API_CONTROLLER.queues.geocoding = [];
      },
      
      check: function() {
        const dailyUsage = API_CONTROLLER.usage.football.day;
        const hourlyUsage = API_CONTROLLER.usage.football.hour;
        
        if (dailyUsage > 450) { // 90% of daily limit
          this.activate('Approaching daily API limit');
          return true;
        }
        
        if (hourlyUsage > 90) { // 90% of hourly limit
          this.activate('Approaching hourly API limit');
          return true;
        }
        
        return false;
      }
    };

    // CONTROLLED API FUNCTIONS - Prevent runaway costs
    const controlledFetch = async (url, options = {}, apiType = 'football', cacheTime = 300000) => {
      const cacheKey = API_CONTROLLER.getCacheKey(url, options);
      
      // Check cache first
      if (API_CONTROLLER.cache.has(cacheKey)) {
        const cached = API_CONTROLLER.cache.get(cacheKey);
        if (Date.now() - cached.timestamp < cacheTime) {
          console.log(`📦 CACHE HIT: ${url}`);
          return cached.data;
        }
      }
      
      // Check rate limits
      if (!API_CONTROLLER.canMakeRequest(apiType)) {
        console.warn(`⚠️ RATE LIMIT: ${apiType} API - Request blocked`);
        throw new Error(`Rate limit exceeded for ${apiType} API`);
      }
      
      try {
        console.log(`🌐 API CALL: ${apiType} - ${url}`);
        API_CONTROLLER.recordRequest(apiType);
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
          throw new Error(`API Error: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Cache the result
        API_CONTROLLER.cache.set(cacheKey, {
          data: data,
          timestamp: Date.now()
        });
        
        return data;
        
      } catch (error) {
        console.error(`❌ API ERROR: ${apiType} - ${error.message}`);
        throw error;
      }
    };

    // Priority queue for essential vs non-essential requests
    const queueApiRequest = (requestFunc, priority = 'normal') => {
      return new Promise((resolve, reject) => {
        const queueItem = {
          execute: requestFunc,
          resolve,
          reject,
          priority,
          timestamp: Date.now()
        };
        
        if (priority === 'high') {
          // High priority requests go to front
          API_CONTROLLER.queues.football.unshift(queueItem);
        } else {
          API_CONTROLLER.queues.football.push(queueItem);
        }
        
        processQueue();
      });
    };

    // Process API queue with delays
    const processQueue = async () => {
      const queue = API_CONTROLLER.queues.football;
      if (queue.length === 0) return;
      
      const item = queue.shift();
      
      try {
        const result = await item.execute();
        item.resolve(result);
      } catch (error) {
        item.reject(error);
      }
      
      // Add delay between requests
      setTimeout(processQueue, 1000); // 1 second between requests
    };

  const CACHE_CONFIG = {
    enabled: true,
    ttl: 6 * 60 * 60 * 1000,
    maxInitialCountries: 15,
    priorityCountries: ['GB', 'US', 'DE', 'FR', 'ES', 'IT', 'BR', 'AR', 'NL', 'PT', 'BE', 'CH', 'SE', 'NO', 'DK'],
    enableProgressiveLoading: true
  };

  // Validate API keys on component mount
  useEffect(() => {
    console.log('🔧 API Configuration Check:');
    console.log('📍 Google Maps API Key:', GOOGLE_KEY ? '✅ Present' : '❌ Missing');
    console.log('⚽ Football API Key:', API_CONFIG.football.key ? '✅ Present' : '❌ Missing');
    
    if (!GOOGLE_KEY) {
      console.error('🚫 Google Maps API key missing! Add REACT_APP_GOOGLE_MAPS_API_KEY to .env');
    }
    if (!API_CONFIG.football.key) {
      console.error('🚫 Football API key missing! Add REACT_APP_FOOTBALL_API_KEY to .env');
    }
  }, []);

  // Fetch Countries from api-football.com (NO HARDCODING)
  useEffect(() => {
    fetchCountriesFromAPI();
  }, []);

  
  const fetchCountriesFromAPI = async () => {
    setIsLoadingCountries(true);
    setApiError(null);

    try {
      console.log('🌐 Fetching competitions from football-data.org...');
      
      // Football-Data.org uses "competitions" instead of "countries"
      let data;
      try {
        data = await fgFootball('competitions');
      } catch (err) {
        console.error('❌ Football proxy /competitions failed:', err);
        throw err;
      }

      if (!data || !Array.isArray(data.competitions)) {
        console.error('❌ Football proxy returned unexpected shape:', data);
        throw new Error('Bad football competitions payload');
      }

      // Extract unique countries from competitions
      const countryMap = new Map();
      data.competitions.forEach(competition => {
        const area = competition.area;
        if (area && area.code && area.name) {
          if (!countryMap.has(area.code)) {
            countryMap.set(area.code, {
              name: area.name,
              code: area.code,
              flag: area.flag || area.ensignUrl,
              competitions: []
            });
          }
          countryMap.get(area.code).competitions.push({
            id: competition.id,
            name: competition.name,
            type: competition.type,
            emblem: competition.emblem
          });
        }
      });

      const countries = Array.from(countryMap.values());
      console.log(`📊 Extracted ${countries.length} unique countries from competitions`);

      const processedCountries = await processCountriesData(countries);
      setCountriesData(processedCountries);
      countriesDataRef.current = processedCountries;
      console.log(`✅ Loaded ${processedCountries.length} countries from API`);
      
    } catch (error) {
      // No fallback country set. countriesData stays empty, which disables
      // hover; the map still renders venues from the cached JSON, which does
      // not depend on this request. A synthetic "Demo Mode" country at 0,0
      // used to be inserted here - it named a cause this catch cannot know
      // and rendered nowhere.
      console.error('❌ Error fetching countries:', error);
      setApiError(error.message);
    } finally {
      setIsLoadingCountries(false);
    }
  };

  // Process API data to include geocoding (NO HARDCODING)
  const processCountriesData = async (apiCountries) => {
    const processedCountries = [];
    
    // SMART LOADING: Start with popular countries, load others on-demand
    const priorityCountries = ['ENG', 'DEU', 'FRA', 'ESP', 'ITA', 'BRA', 'ARG', 'NLD', 'POR', 'BEL'];
    // Create priority countries data with special handling for GB
    const priorityCountriesData = [];

    priorityCountries.forEach(code => {
      const country = apiCountries.find(c => c.code === code);
      if (country) {
        priorityCountriesData.push(country);
      }
    });
    
    // Process priority countries immediately (10 countries = ~30 API calls)
    const countriesToProcess = CACHE_CONFIG.enableProgressiveLoading 
      ? priorityCountriesData 
      : apiCountries.slice(0, CACHE_CONFIG.maxInitialCountries);
      
    console.log(`🌍 SMART LOADING: Processing ${countriesToProcess.length} priority countries first`);
      setTotalCountries(apiCountries.length);
      setLoadingProgress(0);
    
    for (const country of countriesToProcess) {
      try {
        // Update progress
        setLoadingProgress(processedCountries.length + 1);
        
        // Use competitions data directly (no need for separate API call)
        const competitionNames = country.competitions
          ? country.competitions.slice(0, 3).map(c => c.name)
          : [];

        processedCountries.push({
          id: country.code || country.name.replace(/\s+/g, ''),
          name: country.name,
          code: country.code,
          flag: country.flag || null, // football-data.org's area.flag; no flag URL means render nothing
          stadiums: country.competitions ? country.competitions.length * 10 : 0, // Estimate
          topLeagues: competitionNames,
          competitions: country.competitions || [],
        });
      } catch (error) {
        console.warn(`⚠️ Skipping ${country.name}:`, error.message);
      }
    }
    
    return processedCountries;
  };

    // Get leagues for country (NO HARDCODING)
    const getCountryCompetitions = async (countryCode) => {
      try {
        // Football-Data.org doesn't filter by country directly
        // We already have competitions from the initial load
        const country = countriesDataRef.current.find(c => c.code === countryCode);
        return country?.competitions || [];
      } catch (error) {
        console.error('Error getting competitions:', error);
        return [];
      }
    };

    const getCountryNameFromCode = async (countryCode) => {
      console.log(`🔍 COUNTRY NAME LOOKUP: ${countryCode}`);
      
      // Check translation table first
      if (window.countryTranslationTable && window.countryTranslationTable[countryCode]) {
        const translatedName = window.countryTranslationTable[countryCode];
        console.log(`📖 TRANSLATION TABLE HIT: ${countryCode} -> ${translatedName}`);
        return translatedName;
      }
      
      // Special country code mappings for API compatibility
      const specialMappings = {
        'GB': 'England',  // API uses England, not Great Britain
        'US': 'USA',      // API might use USA format
        'KR': 'South-Korea',
        'ZA': 'South-Africa'
      };
      
      if (specialMappings[countryCode]) {
        console.log(`🎯 SPECIAL MAPPING: ${countryCode} -> ${specialMappings[countryCode]}`);
        return specialMappings[countryCode];
      }
      
      // First check existing countries data
      const existingCountry = countriesDataRef.current.find(country => 
        country.code?.toLowerCase() === countryCode?.toLowerCase()
      );
      
      if (existingCountry) {
        return existingCountry.name;
      }
      
      // If not found, fetch from football API to get the correct name
      try {
        const response = await fgFootball('countries');
        
        const data = await response.json();
        
        if (data.response && Array.isArray(data.response)) {
          const country = data.response.find(c => 
            c.code?.toLowerCase() === countryCode?.toLowerCase()
          );
          
          if (country) {
            console.log(`✅ API LOOKUP: ${countryCode} -> ${country.name}`);
            return country.name;
          }
        }
      } catch (error) {
        console.warn(`⚠️ Could not lookup country name for ${countryCode}:`, error.message);
      }
      
      // Last resort: return the code itself
      console.log(`⚠️ NO TRANSLATION: Using ${countryCode} as-is`);
      return countryCode;
    };

    // Get alternative country names from REST Countries API (100% API-driven)
    const getCountryAlternativeNames = async (countryCode) => {
      try {
        console.log(`🔍 LOOKUP: Getting alternative names for ${countryCode}`);
        
        const response = await fetch(`https://restcountries.com/v3.1/alpha/${countryCode}`);
        if (!response.ok) return [];
        
        const data = await response.json();
        const country = data[0];
        
        const alternativeNames = [];
        
        // Get common name
        if (country.name?.common) {
          alternativeNames.push(country.name.common);
        }
        
        // Get official name
        if (country.name?.official) {
          alternativeNames.push(country.name.official);
        }
        
        // Get native names
        if (country.name?.nativeName) {
          Object.values(country.name.nativeName).forEach(nativeName => {
            if (nativeName.common) alternativeNames.push(nativeName.common);
            if (nativeName.official) alternativeNames.push(nativeName.official);
          });
        }
        
        // Get alternative spellings
        if (country.altSpellings) {
          alternativeNames.push(...country.altSpellings);
        }
        
        // Remove duplicates and filter out empty strings
        const uniqueNames = [...new Set(alternativeNames)]
          .filter(name => name && name.length > 0)
          .slice(0, 5); // Limit to 5 attempts to avoid API spam
        
        console.log(`📋 ALTERNATIVES for ${countryCode}:`, uniqueNames);
        return uniqueNames;
        
      } catch (error) {
        console.warn(`⚠️ Could not fetch alternative names for ${countryCode}:`, error.message);
        return [];
      }
    };

    // Fetch detailed stadium data for a country
    const fetchStadiumsForCountry = async (countryCode, countryName) => {
      console.log(`🏟 DEBUG: Starting stadium fetch for ${countryName} (${countryCode})`);
      
      // Check cache first
      if (stadiumsCache[countryCode]) {
        console.log(`🏟️ CACHE HIT: Using cached stadiums for ${countryName}`);
        return stadiumsCache[countryCode];
      }

      setIsLoadingStadiums(true);
      
      try {
        // Get country's competitions
        const country = countriesDataRef.current.find(c => c.code === countryCode);
        if (!country || !country.competitions || country.competitions.length === 0) {
          console.warn(`❌ No competitions found for ${countryName}`);
          return [];
        }

        console.log(`🏆 FOUND: ${country.competitions.length} competitions for ${countryName}`);

        const stadiums = [];
        
        // Get teams from the top competition
        // ✅ FIXED CODE - Prioritize Premier League over Championship
        const premierLeague = country.competitions.find(c => 
          c.name.toLowerCase().includes('premier') || c.id === 2021
        );

        const topCompetition = premierLeague || country.competitions[0];
        console.log(`🏆 TOP COMPETITION: ${topCompetition.name} (ID: ${topCompetition.id}) ${premierLeague ? '[PREMIER LEAGUE]' : '[FALLBACK]'}`);
        
        try {
          const teamsData = await fgFootball(`competitions/${topCompetition.id}/teams`);
          
          if (teamsData && teamsData.teams && Array.isArray(teamsData.teams)) {
            console.log(`⚽ FOUND: ${teamsData.teams.length} teams in ${topCompetition.name}`);
            
            // 🔥 FIX: Proper country name for geocoding
            const geocodingCountry = countryCode === 'ENG' ? 'United Kingdom' : countryName;
            console.log(`🌍 GEOCODING COUNTRY: ${countryName} → ${geocodingCountry}`);
            
            for (const team of teamsData.teams) {
              if (team.venue) {
                try {
                  // 🔥 FIX: Add delay BEFORE each geocoding attempt
                  await new Promise(resolve => setTimeout(resolve, 1000));
                  
                  // 🔥 CRITICAL FIX: Extract city from address properly
                  let city = team.venue; // Default: use venue name as city
                  
                  // Try to extract city from address if it exists
                  if (team.address && team.address.trim() !== '') {
                    // Address format: "Stadium Name City Postcode"
                    // Example: "Ewood Park Blackburn BB2 4JF"
                    const addressParts = team.address.split(' ');
                    
                    // City is usually before the postcode (postcode has numbers)
                    const cityIndex = addressParts.findIndex((part, idx) => 
                      idx > 0 && /\d/.test(addressParts[idx + 1])
                    );
                    
                    if (cityIndex > 0) {
                      city = addressParts[cityIndex];
                    }
                  }
                  
                  // 🔥 CRITICAL FIX: Use VENUE (stadium name), not TEAM name
                  console.log(`🏟️ GEOCODING: ${team.venue} in ${city}, ${geocodingCountry}`);
                  
                  const coordinates = await geocodeStadium(
                    team.address,      // Full address
                    city,              // ✅ City name (extracted)
                    team.venue,        // ✅ STADIUM NAME (not team.name!)
                    geocodingCountry   // ✅ "United Kingdom"
                  );
                  
                  if (coordinates) {
                    stadiums.push({
                      id: team.id,
                      name: team.venue || `${team.name} Stadium`,
                      address: team.address || team.venue,
                      city: city,
                      capacity: 0,
                      coordinates: coordinates,
                      team: team.name,
                      teamLogo: team.crest,
                      league: topCompetition.name,
                      leagueId: topCompetition.id,
                      isTopLeague: true
                    });
                    
                    console.log(`✅ STADIUM ADDED: ${team.venue} → ${team.name} (${coordinates.lat}, ${coordinates.lng})`);
                  } else {
                    console.warn(`⚠️ No coordinates for ${team.name}: ${team.venue}`);
                  }
                } catch (error) {
                  console.warn(`⚠️ Failed to process ${team.name}:`, error.message);
                }
              }
            }
          }
        } catch (error) {
          console.warn(`⚠️ Failed to get teams for ${topCompetition.name}:`, error.message);
        }

        console.log(`🎯 RESULT: ${stadiums.length} stadiums found`);
        // Fetch league standings
        try {
          console.log('📊 Fetching league standings for competition:', topCompetition.id);
          const standingsData = await fgFootballStandings(topCompetition.id);
          setStandings(standingsData.standings);
          console.log('📊 STANDINGS:', standingsData.standings);
        } catch (error) {
          console.error('❌ Failed to fetch standings:', error);
          setStandings(null);
        }
        
        // Cache the results
        setStadiumsCache(prev => ({
          ...prev,
          [countryCode]: stadiums
        }));
        
        return stadiums;
        
      } catch (error) {
        console.error(`❌ Stadium fetch failed for ${countryName}:`, error);
        return [];
      } finally {
        setIsLoadingStadiums(false);
      }
    };

    // Load stadiums for specific league
    const fetchStadiumsForLeague = async (countryCode, leagueId, leagueName) => {
      const apiCountryName = await getCountryNameFromCode(countryCode);
      console.log(`🔍 LEAGUE API Country Name: ${countryCode} -> ${apiCountryName}`);
      console.log(`🏟️ LEAGUE-SPECIFIC: Loading stadiums for ${leagueName} (ID: ${leagueId})`);
      
      setIsLoadingStadiums(true);
      
      try {
        // Use current season consistently across all functions
        const currentSeason = new Date().getFullYear();
        const teamsResponse = await fgFootball('teams', { 
          league: leagueId, 
          season: currentSeason 
        });
        
        if (!teamsResponse.response) {
          console.warn(`❌ No teams found for league ${leagueName}`);
          return [];
        }
        
        console.log(`⚽ TEAMS: Found ${teamsResponse.response.length} teams in ${leagueName}`);
        
        const stadiums = [];
        
        // Process each team to get their stadium
        for (const teamData of teamsResponse.response.slice(0, 20)) { // Limit to prevent API overload
          const team = teamData.team;
          const venue = teamData.venue;
          
          // 🔥 DEBUG: Log all teams to see what's missing
          console.log(`👥 TEAM: ${team.name} | Venue: ${venue?.name || 'NO VENUE'} | City: ${venue?.city || 'NO CITY'}`);
          
          if (venue && venue.name && venue.city) {
            console.log(`🏟️ PROCESSING: ${venue.name} (${team.name})`);
            
            try {
              const coordinates = await geocodeStadium(venue.address, venue.city, venue.name, apiCountryName);
              
              if (coordinates) {
                stadiums.push({
                  id: venue.id,
                  name: venue.name,
                  address: venue.address || `${venue.city}`,
                  city: venue.city,
                  capacity: venue.capacity || 0,
                  coordinates: coordinates,
                  image: venue.image,
                  surface: venue.surface || 'Grass',
                  team: team.name,
                  teamLogo: team.logo,
                  league: leagueName,
                  leagueId: leagueId
                });
                
                console.log(`✅ STADIUM ADDED: ${venue.name} -> ${team.name}`);
              }
            } catch (error) {
              console.warn(`⚠️ Failed to process ${venue.name}:`, error.message);
            }
          } else {
            // 🔥 API-DRIVEN FALLBACK: Use team name as location (no hardcoding)
            console.log(`🔄 FALLBACK: Creating stadium for ${team.name} using team name as location`);
            
            try {
              // Extract base location from team name (remove " W" suffix for women's teams)
              const teamBaseName = team.name.replace(/ W$/, '').replace(/ Women$/, '');
              const searchLocation = `${teamBaseName}, ${team.country}`;
              const fallbackStadiumName = `${teamBaseName} Stadium`;
              
              console.log(`📍 GEOCODING: ${searchLocation}`);
              
              // Use team name + country for geocoding
              const coordinates = await geocodeStadium(null, searchLocation, fallbackStadiumName, apiCountryName);
              
              if (coordinates) {
                stadiums.push({
                  id: `fallback_${team.id}`,
                  name: fallbackStadiumName,
                  address: searchLocation,
                  city: teamBaseName,
                  capacity: 0,
                  coordinates: coordinates,
                  image: null,
                  surface: 'Grass',
                  team: team.name,
                  teamLogo: team.logo,
                  league: leagueName,
                  leagueId: leagueId,
                  isFallback: true
                });
                
                console.log(`✅ FALLBACK STADIUM ADDED: ${fallbackStadiumName} -> ${team.name}`);
              } else {
                console.log(`⚠️ Could not geocode fallback for ${team.name}`);
              }
            } catch (error) {
              console.warn(`⚠️ Fallback failed for ${team.name}:`, error.message);
            }
          }
        }
        
        console.log(`🎯 LEAGUE RESULT: ${stadiums.length} stadiums loaded for ${leagueName}`);
        return stadiums;
        
      } catch (error) {
        console.error(`❌ League stadium fetch failed:`, error);
        return [];
      } finally {
        setIsLoadingStadiums(false);
      }
    };

    // API-DRIVEN: Automatically rank leagues by importance (NO HARDCODING)
    const rankLeaguesByImportance = (leagues) => {
      return leagues.map(league => {
        let importanceScore = 0;
        const currentSeason = league.seasons?.find(s => s.current) || league.seasons?.[0];
        
        // 🔥 PRIMARY FACTOR: Coverage depth (most reliable indicator)
        if (currentSeason?.coverage?.fixtures) {
          const coverage = currentSeason.coverage.fixtures;
          const coverageCount = Object.values(coverage).filter(v => v === true).length;
          importanceScore += coverageCount * 50; // 0-200 points based on API coverage
          
          console.log(`📊 COVERAGE: ${league.league.name} = ${coverageCount}/4 coverage (${coverageCount * 50} points)`);
        }
        
        // 🔥 SECONDARY FACTOR: League ID (lower = more established)
        if (league.league.id < 200) {
          importanceScore += 100; // Very low ID = likely top tier
        } else if (league.league.id < 500) {
          importanceScore += 50;  // Medium ID = likely secondary
        } else {
          importanceScore += 10;  // High ID = likely lower tier
        }
        
        // 🔥 TERTIARY FACTORS: Basic API properties
        if (league.league.type === 'League') importanceScore += 30;
        if (league.league.type === 'Cup') importanceScore += 15;
        if (currentSeason?.current) importanceScore += 20;
        
        // 🔥 WOMEN'S LEAGUE BOOST: If coverage is full, boost women's leagues
        const isWomensLeague = league.league.name.toLowerCase().includes('women') || 
                              league.league.name.toLowerCase().includes('toppserien') ||
                              league.league.name.toLowerCase().includes('female');
        
        if (isWomensLeague && currentSeason?.coverage?.fixtures) {
          const coverageCount = Object.values(currentSeason.coverage.fixtures).filter(v => v === true).length;
          if (coverageCount >= 3) { // High coverage women's league
            importanceScore += 80; // Boost to compete with men's top leagues
          }
        }
        
        console.log(`🎯 FINAL SCORE: ${league.league.name} = ${importanceScore} points`);
        
        return {
          ...league,
          importanceScore
        };
      }).sort((a, b) => b.importanceScore - a.importanceScore);
    };

    // Enhanced geocoding with country context and multiple fallback strategies:
    const geocodeStadium = async (address, city, stadiumName, countryName) => {
      try {
        // Strategy 1: Full address if available
        if (address && address !== 'null' && address !== '') {
          const fullQuery = `${stadiumName}, ${address}, ${city}, ${countryName}`;
          console.log(`🔍 GEOCODING STRATEGY 1: "${fullQuery}"`);
          
          const data = await fgForwardGeocode(fullQuery);
          
          // 🔥 DEBUG: Log what Google actually returns
          console.log(`📡 GOOGLE RESPONSE (Strategy 1):`, {
            status: data?.status,
            resultsCount: data?.results?.length || 0,
            error: data?.error_message
          });
          
          if (data && data.status === 'OK' && data.results && data.results[0]) {
            const location = data.results[0].geometry.location;
            console.log(`✅ STRATEGY 1 SUCCESS: ${stadiumName} → ${location.lat}, ${location.lng}`);
            return { lat: location.lat, lng: location.lng };
          }
        }
        
        // Strategy 2: Stadium + City + Country
        const cityQuery = `${stadiumName}, ${city}, ${countryName}`;
        console.log(`🔍 GEOCODING STRATEGY 2: "${cityQuery}"`);
        
        const data2 = await fgForwardGeocode(cityQuery);
        
        // 🔥 DEBUG: Log what Google returns
        console.log(`📡 GOOGLE RESPONSE (Strategy 2):`, {
          status: data2?.status,
          resultsCount: data2?.results?.length || 0,
          error: data2?.error_message
        });
        
        if (data2 && data2.status === 'OK' && data2.results && data2.results[0]) {
          const location = data2.results[0].geometry.location;
          console.log(`✅ STRATEGY 2 SUCCESS: ${stadiumName} → ${location.lat}, ${location.lng}`);
          return { lat: location.lat, lng: location.lng };
        }
        
        // Strategy 3: Just city center as fallback
        const cityOnly = `${city}, ${countryName}`;
        console.log(`🔍 GEOCODING STRATEGY 3 (City Center): "${cityOnly}"`);
        
        const data3 = await fgForwardGeocode(cityOnly);
        
        // 🔥 DEBUG: Log what Google returns
        console.log(`📡 GOOGLE RESPONSE (Strategy 3):`, {
          status: data3?.status,
          resultsCount: data3?.results?.length || 0,
          error: data3?.error_message
        });
        
        if (data3 && data3.status === 'OK' && data3.results && data3.results[0]) {
          const location = data3.results[0].geometry.location;
          console.log(`✅ STRATEGY 3 SUCCESS (City Center): ${city} → ${location.lat}, ${location.lng}`);
          return { lat: location.lat, lng: location.lng };
        }
        
        console.warn(`❌ ALL GEOCODING STRATEGIES FAILED for ${stadiumName}`);
        return null;
        
      } catch (error) {
        if (error.message.includes('Rate limit')) {
          console.warn(`⚠️ GEOCODING RATE LIMITED: ${stadiumName}`);
          return null;
        }
        console.warn(`Geocoding failed for ${stadiumName}:`, error);
        return null;
      }
    };

    // TRANSLATION TABLE BUILDER - API-driven country name mapping
    const buildCountryTranslationTable = async () => {
      console.log('🔄 BUILDING: Dynamic country translation table from APIs...');
      
      try {
        // Get all countries from Football API
        const footballData = await fgFootball('countries');
        
        if (!footballData.response) {
          console.error('❌ Failed to get Football API countries');
          return {};
        }
        
        console.log(`📊 Football API countries: ${footballData.response.length}`);
        
        // Get Google/REST Countries data (we already have some from our existing countries)
        const translationTable = {};
        const unmatchedCountries = [];
        
        // Try to match each of our loaded countries with Football API
        for (const ourCountry of countriesDataRef.current) {
          console.log(`🔍 PROCESSING: ${ourCountry.code} (${ourCountry.name})`);
          
          const footballMatch = footballData.response.find(fc => {
            const basicMatch = fc.code === ourCountry.code ||
                              fc.name?.toLowerCase() === ourCountry.name?.toLowerCase() ||
                              fc.code === ourCountry.name ||
                              isNameSimilar(fc.name, ourCountry.name);
            
            const specialMatch = (ourCountry.code === 'GB' && fc.code === 'GB-ENG');
            
            if (specialMatch) {
              console.log(`🎯 SPECIAL MATCH: ${ourCountry.code} -> ${fc.code} (${fc.name})`);
            }
            
            return basicMatch || specialMatch;
          });
          
          if (footballMatch) {
            translationTable[ourCountry.code] = footballMatch.name;
            console.log(`✅ MATCH: ${ourCountry.code} (${ourCountry.name}) -> ${footballMatch.name}`);
          } else {
            unmatchedCountries.push(ourCountry);
            console.log(`❌ NO MATCH: ${ourCountry.code} (${ourCountry.name})`);
            
            // Special debug for GB
            if (ourCountry.code === 'GB') {
              console.log('🔍 GB DEBUG: Looking for GB-ENG in API...');
              const gbEng = footballData.response.find(fc => fc.code === 'GB-ENG');
              if (gbEng) {
                console.log(`🎯 FOUND GB-ENG: ${gbEng.code} -> ${gbEng.name}`);
                translationTable[ourCountry.code] = gbEng.name;
                console.log(`🔧 FORCED GB MAPPING: ${ourCountry.code} -> ${gbEng.name}`);
              } else {
                console.log('❌ GB-ENG not found in API');
              }
            }
          }
        }
        
        // Store the table globally
        window.countryTranslationTable = translationTable;
        
        console.log('📋 TRANSLATION TABLE COMPLETE:');
        console.log(`   ✅ Matched: ${Object.keys(translationTable).length}`);
        console.log(`   ❌ Unmatched: ${unmatchedCountries.length}`);
        console.log('📖 Translation table:', translationTable);
        
        if (unmatchedCountries.length > 0) {
          console.log('⚠️ UNMATCHED COUNTRIES:', unmatchedCountries.map(c => `${c.code}:${c.name}`));
        }
        
        return translationTable;
        
      } catch (error) {
        console.error('❌ Translation table build failed:', error);
        return {};
      }
    };

    // Helper function for name similarity
    const isNameSimilar = (name1, name2) => {
      if (!name1 || !name2) return false;
      
      const normalize = str => str.toLowerCase().replace(/[^a-z]/g, '');
      const n1 = normalize(name1);
      const n2 = normalize(name2);
      
      return n1 === n2 || 
            n1.includes(n2) || 
            n2.includes(n1) ||
            // Special cases
            (n1.includes('kingdom') && n2.includes('england')) ||
            (n1.includes('states') && n2.includes('usa'));
    };

    

  // Get map styles based on zoom level
  const getMapStylesForZoom = (zoomLevel) => {
    if (zoomLevel <= 4) {
      // WORLD VIEW: Your clean, minimal style
      return [
        // Remove all labels and clutter
        {
          "featureType": "all",
          "elementType": "labels",
          "stylers": [{"visibility": "off"}]
        },
        // Clean water
        {
          "featureType": "water",
          "elementType": "geometry",
          "stylers": [{"color": "#a2daf2"}]
        },
        // Clean land
        {
          "featureType": "landscape",
          "elementType": "geometry",
          "stylers": [{"color": "#f5f5f2"}, {"lightness": 20}]
        },
        // Subtle country borders only
        {
          "featureType": "administrative.country",
          "elementType": "geometry.stroke",
          "stylers": [{"color": "#c9b2a6"}, {"weight": 1}]
        },
        // Hide roads completely
        {
          "featureType": "road",
          "stylers": [{"visibility": "off"}]
        },
        // Hide points of interest
        {
          "featureType": "poi",
          "stylers": [{"visibility": "off"}]
        },
        // Hide transit
        {
          "featureType": "transit",
          "stylers": [{"visibility": "off"}]
        },
        // Hide state/province borders
        {
          "featureType": "administrative.province",
          "stylers": [{"visibility": "off"}]
        },
        {
          "featureType": "administrative.locality",
          "stylers": [{"visibility": "off"}]
        },
        {
          "featureType": "administrative.neighborhood",
          "stylers": [{"visibility": "off"}]
        },
        // Remove internal borders within countries
        {
          "featureType": "administrative.land_parcel",
          "stylers": [{"visibility": "off"}]
        }
      ];
    } else {
      // COUNTRY ZOOM VIEW: Detailed with cities, terrain, roads
      return [
        // Beautiful water styling
        {
          "featureType": "water",
          "elementType": "geometry",
          "stylers": [
            {"color": "#4a90e2"},
            {"lightness": 10}
          ]
        },
        // Natural landscape with terrain visibility
        {
          "featureType": "landscape",
          "elementType": "geometry",
          "stylers": [
            {"color": "#f0f8e8"},
            {"lightness": 15}
          ]
        },
        // Show parks and green areas
        {
          "featureType": "poi.park",
          "elementType": "geometry",
          "stylers": [
            {"color": "#7cb342"},
            {"lightness": 30},
            {"visibility": "on"}
          ]
        },
        // Country borders (stronger when zoomed)
        {
          "featureType": "administrative.country",
          "elementType": "geometry.stroke",
          "stylers": [
            {"color": "#2d5aa0"},
            {"weight": 2}
          ]
        },
        // Show major roads
        {
          "featureType": "road.highway",
          "elementType": "geometry",
          "stylers": [
            {"visibility": "simplified"},
            {"color": "#ffc107"},
            {"weight": 2}
          ]
        },
        // Show arterial roads
        {
          "featureType": "road.arterial",
          "elementType": "geometry",
          "stylers": [
            {"visibility": "simplified"},
            {"color": "#ffffff"},
            {"weight": 1}
          ]
        },
        // CITY LABELS - Fixed feature type
        {
          "featureType": "administrative.locality",
          "elementType": "labels.text.fill",
          "stylers": [
            {"visibility": "on"},
            {"color": "#2c3e50"},
          ]
        },
        {
          "featureType": "administrative.locality",
          "elementType": "labels.text.stroke", 
          "stylers": [
            {"visibility": "on"},
            {"color": "#ffffff"},
            {"weight": 2}
          ]
        },
        // Administrative labels for important cities
        {
          "featureType": "administrative.locality",
          "elementType": "labels.text.fill",
          "stylers": [
            {"visibility": "on"},
            {"color": "#34495e"}
          ]
        },
        // Hide minor POIs but keep important ones
        {
          "featureType": "poi.business",
          "stylers": [{"visibility": "off"}]
        },
        {
          "featureType": "poi.medical",
          "stylers": [{"visibility": "simplified"}]
        },
        {
          "featureType": "poi.school",
          "stylers": [{"visibility": "simplified"}]
        }
      ];
    }
  };

  // GLOBAL FUNCTION: Handle popup button clicks
  // countryKey is the exact stadiums-premium.json key ("South-Africa"). It is the
  // country's identity end to end: no 3-letter code is derived from it.
  window.clickCountryFromPopup = async (countryKey) => {
    console.log(`🎯 CLICKED: ${countryKey} - Using cache!`);
    setIsLoading(true);
    setSelectedCountry(countryKey);

    const countryData = cachedStadiums?.countries?.[countryKey];
    if (!countryData) {
      console.error('❌ Country key not present in stadium cache:', countryKey);
      setIsLoading(false);
      return;
    }

    const selectedCountryName = countryKey;
    console.log(`🔍 Looking up: "${countryKey}"`);

    // Get ALL stadiums for this country from cache
    const allStadiumsForCountry = getStadiumsFromCache(countryKey);

    const topLeague = countryData?.leagues?.[0]; // First league = top priority (Premier League, Bundesliga, etc)
    
    // Filter to TOP LEAGUE ONLY
    const cachedStadiumsForCountry = topLeague 
      ? allStadiumsForCountry.filter(stadium => stadium.leagueId === topLeague.id)
      : allStadiumsForCountry;
    
    console.log(`⚡ Loaded ${cachedStadiumsForCountry.length} stadiums for ${selectedCountryName} from cache`);
    console.log(`🏆 Showing: ${topLeague?.name || 'All leagues'} (filtered from ${allStadiumsForCountry.length} total)`);

    // Transform cached data to match your existing format
    const stadiumsWithCoords = cachedStadiumsForCountry
      .map(stadium => {
        
        
        // Extract coordinates (handle both formats)
        const lat = stadium.latitude || stadium.lat;
        const lng = stadium.longitude || stadium.lng;
        
        // Skip if no coordinates
        if (!lat || !lng) {
          console.warn(`⚠️ No coordinates for ${stadium.venue || stadium.name}`);
          return null;
        }
        
        return {
          // Spread first so fields the cache carries but this list never named
          // (area, website, tla, shortName, fullAddress...) reach the popup
          // instead of being silently dropped. Every named field below still
          // overrides the spread, so their fallbacks and coercions are unchanged.
          ...stadium,
          name: stadium.venue || stadium.name,
          team: stadium.teamName || stadium.team,
          lat: parseFloat(lat),
          lng: parseFloat(lng),
          coordinates: { lat: parseFloat(lat), lng: parseFloat(lng) },
          address: stadium.address || stadium.fullAddress || '',
          city: stadium.city || '',
          country: stadium.area?.name || '',
          capacity: stadium.capacity || 0,
          leagueName: stadium.leagueName,
          leagueId: stadium.leagueId,
          id: stadium.teamId || stadium.id,
          crestUrl: stadium.crestUrl,
          clubColors: stadium.clubColors,
          founded: stadium.founded
        };
      })
      .filter(stadium => stadium !== null); // Remove null entries

    

    // Clear ALL stadium markers (unified system)
    clearAllStadiumMarkers();

    // Initialize marker array BEFORE creating markers
    if (!window.currentStadiumMarkers) {
      window.currentStadiumMarkers = [];
    }

    // Create new markers directly
      stadiumsWithCoords.forEach((stadium, index) => {
        // Validate coordinates exist
        if (!stadium.lat || !stadium.lng) {
          console.warn(`⚠️ Stadium "${stadium.name}" missing coordinates`);
          return;
        }

        // 🔥 CRITICAL: Validate coordinate ranges for Portugal
        // 🔥 CRITICAL: Validate coordinate ranges for Portugal (including Azores & Madeira)
        if (countryKey === 'Portugal') {
          // Mainland Portugal: 36-43°N, 10-6°W
          // Madeira: 32-33°N, 16-17°W
          // Azores: 37-40°N, 25-31°W
          const validLat = stadium.lat >= 32 && stadium.lat <= 43;    // Include all Portuguese territories
          const validLng = stadium.lng >= -31 && stadium.lng <= -6;   // Include Azores and Madeira
          
          if (!validLat || !validLng) {
            console.error(`❌ INVALID COORDS for ${stadium.name}:`, {
              lat: stadium.lat,
              lng: stadium.lng,
              expected: 'lat: 36-43, lng: -10 to -6'
            });
            return; // Skip this marker
          }
        }

        
      const marker = new window.google.maps.Marker({
        position: { lat: stadium.lat, lng: stadium.lng },
        map: googleMapRef.current,
        title: `${stadium.name} - ${stadium.team}`,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="36" height="48" viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              <!-- Outer glow -->
              <circle cx="18" cy="16" r="14" fill="#22c55e" opacity="0.3" filter="url(#glow)"/>
              
              <!-- Main pin shape -->
              <path d="M 18 2 C 10.268 2 4 8.268 4 16 C 4 24 18 44 18 44 C 18 44 32 24 32 16 C 32 8.268 25.732 2 18 2 Z" 
                    fill="url(#gradient)" 
                    stroke="#ffffff" 
                    stroke-width="2.5"
                    filter="url(#glow)"/>
              
              <!-- Gradient definition -->
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#22c55e;stop-opacity:1" />
                  <stop offset="100%" style="stop-color:#16a34a;stop-opacity:1" />
                </linearGradient>
              </defs>
              
              <!-- Inner stadium icon -->
              <g transform="translate(18, 16)">
                <!-- Stadium structure -->
                <ellipse cx="0" cy="0" rx="7" ry="5" fill="#ffffff" opacity="0.9"/>
                <ellipse cx="0" cy="0" rx="5" ry="3" fill="#22c55e"/>
                
                <!-- Field lines -->
                <line x1="-5" y1="0" x2="5" y2="0" stroke="#ffffff" stroke-width="0.5"/>
                <circle cx="0" cy="0" r="1.5" fill="none" stroke="#ffffff" stroke-width="0.5"/>
              </g>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(36, 48),
          anchor: new window.google.maps.Point(18, 44)
        },
        animation: window.google.maps.Animation.DROP
      });

      // Tag the marker with its team id so match-day fixture data (loaded async,
      // below) can be correlated back to the right marker after creation.
      marker.stadiumTeamId = stadium.id;

      // Add click listener to show info
      marker.addListener('click', () => {
        // Under 768px, use our own bottom-sheet overlay instead of Google's InfoWindow
        if (window.innerWidth < 768) {
          setMobileStadiumCard(stadium);
          return;
        }

        const infoWindow = new window.google.maps.InfoWindow({
          content: createProfessionalStadiumPopup(stadium),
          maxWidth: 350
        });

        // Close other info windows
        if (window.currentStadiumInfoWindow) {
          window.currentStadiumInfoWindow.close();
        }

        infoWindow.open(googleMapRef.current, marker);
        window.currentStadiumInfoWindow = infoWindow;
      });

      // Push to the unified marker array
      window.currentStadiumMarkers.push(marker);
    });

    console.log(`✅ Created ${window.currentStadiumMarkers.length} stadium markers!`);

    // Update sidebar with stadium count
    setStadiumPins(stadiumsWithCoords);
    console.log(`📊 Updated sidebar: ${stadiumsWithCoords.length} stadiums`);

    // The dropdown must name the league whose pins were just drawn, and those
    // are topLeague's. This used to live inside the standings-success arm
    // below, so a country whose top league has no cached standings - which is
    // nearly all of them, the standings cache holds 9 football-data.org ids
    // while stadiums are keyed by API-Football ids - left selectedLeague null
    // and the selector fell back to its "All leagues" default while top-league
    // pins sat on the map. Selecting the league is about what is displayed, not
    // about whether a table happened to be cached, so it belongs here.
    setSelectedLeague(topLeague ? topLeague.id.toString() : null);

    // Get standings from cache (use transformed data which has leagueId)
    if (stadiumsWithCoords.length > 0 && stadiumsWithCoords[0].leagueId) {
      const leagueId = stadiumsWithCoords[0].leagueId;
      const leagueStandings = getStandingsFromCache(leagueId);
      
      if (leagueStandings) {
        // Extract the correct nested structure for your UI
        const standingsData = {
          competition: leagueStandings.competition,
          standings: leagueStandings.standings
        };
        
        setStandings([standingsData]); // Your UI expects array
        
        console.log(`⚡ STANDINGS: Loaded ${leagueStandings.standings.table.length} teams for ${leagueStandings.competition.name}`);
        console.log('📊 First team:', leagueStandings.standings.table[0].team.name, '-', leagueStandings.standings.table[0].points, 'pts');
      } else {
        console.warn(`⚠️ No standings found for league ${leagueId}`);
        setStandings(null);
      }
      
      // Update available leagues list - show leagues from this country
      if (countryData && countryData.leagues) {
        const countryLeagues = countryData.leagues.map(league => ({
          id: league.id,
          name: league.name,
          country: countryKey
        }));
        
        setAvailableLeagues(countryLeagues);
        console.log(`🏆 Available leagues for ${selectedCountryName}: ${countryLeagues.length}`);
      }
    } else {
      console.warn('⚠️ No leagueId found in stadium data');
    }
    
    // Match-day highlighting: fetch upcoming fixtures for this league and recolor
    // markers for teams playing within the next 7 days. Fetched in parallel (not
    // awaited) so it never delays marker/map rendering above; markers already look
    // and behave exactly as they do today by the time this resolves or fails.
    if (topLeague) {
      const today = new Date();
      const weekOut = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      const toYMD = (d) => d.toISOString().slice(0, 10);

      fgFootballMatches(topLeague.id, toYMD(today), toYMD(weekOut))
        .then((fixturesData) => {
          const matchDayByTeamId = new Map();
          (fixturesData.matches || []).forEach((match) => {
            if (match.homeTeam && match.homeTeam.id != null) {
              matchDayByTeamId.set(match.homeTeam.id, {
                utcDate: match.utcDate,
                opponent: match.awayTeam?.name || null
              });
            }
          });

          (window.currentStadiumMarkers || []).forEach((marker) => {
            if (matchDayByTeamId.has(marker.stadiumTeamId)) {
              marker.setIcon(buildMatchDayMarkerIcon());
            }
          });

          // Stashed for createProfessionalStadiumPopup, which renders on-demand
          // (on marker click) and needs this same fixture data at that later point.
          window.currentMatchDayByTeamId = matchDayByTeamId;
        })
        .catch((err) => {
          console.warn('⚠️ Match-day fixtures fetch failed, markers unchanged:', err);
        });
    }

    // Auto-zoom to fit all stadiums
    console.log('🔍 ZOOM CHECK:', {
      hasArray: !!window.currentStadiumMarkers,
      length: window.currentStadiumMarkers?.length,
      hasMap: !!googleMapRef.current
    });

    if (window.currentStadiumMarkers && 
        Array.isArray(window.currentStadiumMarkers) && 
        window.currentStadiumMarkers.length > 0 && 
        googleMapRef.current) {
      
      try {
        const bounds = new window.google.maps.LatLngBounds();
        
        window.currentStadiumMarkers.forEach(marker => {
          if (marker && marker.getPosition) {
            bounds.extend(marker.getPosition());
          }
        });
        
        googleMapRef.current.fitBounds(bounds);
        
        // Add slight padding after fitBounds completes
        setTimeout(() => {
          const currentZoom = googleMapRef.current.getZoom();
          if (currentZoom > 7) {
            googleMapRef.current.setZoom(7); // Max zoom for country view
          }
        }, 300);
        
        console.log(`🗺️ Zoomed to fit ${window.currentStadiumMarkers.length} stadiums`);
      } catch (error) {
        console.error('❌ ZOOM ERROR:', error);
      }
    }

    setIsLoading(false);
    console.log(`✅ COMPLETE: ${countryKey} loaded in <1 second!`);
  };
  const initializeMap = () => {
    (async () => {
      if (!window.google || !mapRef.current) return;
      console.log('🚀 Creating Google Maps instance...');
      console.log('🔍 mapRef.current:', mapRef.current);
      console.log('🔍 mapRef.current.innerHTML:', mapRef.current.innerHTML);
      console.log('🔍 mapRef.current.children.length:', mapRef.current.children.length);
      console.log('🔍 window.google.maps:', typeof window.google.maps);

    
    
      // Clear any existing map content
      mapRef.current.innerHTML = '';

      // Prefer legacy constructor if present
      let MapCtor = window.google?.maps?.Map;
      // Capture the namespace alongside the constructor. Every google.maps.*
      // read that happens after an await must come from this reference, never
      // from the mutable global - that pairing is what keeps class identities
      // consistent, so a Polygon built minutes later still accepts this Map.
      let gmaps = window.google?.maps;

if (typeof MapCtor !== 'function') {
  if (window.google?.maps?.importLibrary) {
    const mapsModule = await window.google.maps.importLibrary('maps'); // { Map, ... }
    MapCtor = mapsModule && mapsModule.Map;
    gmaps = window.google?.maps; // re-pair after the await
  }
}

if (typeof MapCtor !== 'function') {
  console.error('Google Maps Map constructor unavailable. google.maps =', window.google?.maps);
  return; // stop; prevents "is not a constructor"
}

const map = new MapCtor(mapRef.current, {
  // keep your existing options (center, zoom, mapId, etc.)



        zoom: 2,
        center: { lat: 20, lng: 0 },
        mapTypeId: 'roadmap',
        styles: getMapStylesForZoom(2),
        disableDefaultUI: true,  // CHANGED: Hide all default UI
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        clickableIcons: false,
        keyboardShortcuts: false  // ADD: Disable keyboard shortcuts UI
      });

      // Enhanced zoom change listener with smart state management
      map.addListener('zoom_changed', () => {
        const currentZoom = map.getZoom();
        setZoomLevel(currentZoom);
        
        // Smart mode detection based on zoom
        let newMode = 'world';
        if (currentZoom >= 8) {
          newMode = 'stadium';
        } else if (currentZoom >= 5) {
          newMode = 'country';
        }
        
        if (newMode !== appMode) {
          setAppMode(newMode);
          console.log(`🎯 MODE CHANGE: ${appMode} → ${newMode} (zoom: ${currentZoom})`);
        }
        
        // Enhanced map options for stadium viewing
        const mapOptions = {
          styles: getMapStylesForZoom(currentZoom)
        };
        
        // Enable 3D buildings and satellite view when close to stadiums
        if (currentZoom >= 15) {
          mapOptions.mapTypeId = 'hybrid';
          mapOptions.tilt = 45;
        } else if (currentZoom >= 10) {
          mapOptions.mapTypeId = 'roadmap';
          mapOptions.tilt = 0;
        }
        
        map.setOptions(mapOptions);
      });

    // Enable mousemove events explicitly
    map.setOptions({ 
      disableDoubleClickZoom: false,
      draggable: true,
      scrollwheel: true,
      disableDefaultUI: false
    });

    // Enable data layer for country highlighting
    map.data.setStyle({
      fillOpacity: 0,
      strokeOpacity: 0,
      clickable: false
    });
    
    // Force Google Maps to initialize mousemove events
    gmaps.event.trigger(map, 'resize');
    
    // Add a small delay before setting up hover effects
    setTimeout(() => {
      setupCountryHoverEffects(map);
    }, 100);

   googleMapRef.current = map;

    // DEBUG: Add comprehensive Google attribution debugging
    const debugGoogleAttribution = () => {
      console.log('🔍 DEBUG: Checking Google attribution elements...');
      
      // Check in map container
      const mapContainer = mapRef.current;
      console.log('📦 Map container:', mapContainer);
      
      // Check for Google elements with multiple selectors
      const selectors = [
        'a[href*="maps.google.com"]',
        '.gm-style-cc',
        '.gmnoprint',
        '[title="Google"]',
        'img[alt="Google"]'
      ];
      
      selectors.forEach(selector => {
        const elementsInMap = mapContainer?.querySelectorAll(selector);
        const elementsInDocument = document.querySelectorAll(selector);
        console.log(`🔍 ${selector}:`);
        console.log(`   In map: ${elementsInMap?.length || 0} elements`);
        console.log(`   In document: ${elementsInDocument?.length || 0} elements`);
        
        if (elementsInDocument?.length > 0) {
          elementsInDocument.forEach((el, index) => {
            console.log(`   Element ${index}:`, {
              display: el.style.display,
              visibility: el.style.visibility,
              opacity: el.style.opacity,
              position: el.style.position,
              bottom: el.style.bottom,
              left: el.style.left,
              zIndex: el.style.zIndex,
              innerHTML: el.innerHTML?.substring(0, 50)
            });
          });
        }
      });
      
      // Check all children in map container
      if (mapContainer) {
        console.log('📋 All children in map container:');
        const allChildren = mapContainer.querySelectorAll('*');
        console.log(`   Total children: ${allChildren.length}`);
        
        // Look for any Google-related elements
        const googleElements = Array.from(allChildren).filter(el => 
          el.innerHTML?.includes('Google') || 
          el.getAttribute('href')?.includes('google') ||
          el.className?.includes('gm-') ||
          el.getAttribute('title')?.includes('Google')
        );
        
        console.log(`   Google-related elements: ${googleElements.length}`);
        googleElements.forEach((el, index) => {
          console.log(`   Google element ${index}:`, {
            tagName: el.tagName,
            className: el.className,
            href: el.getAttribute('href'),
            title: el.getAttribute('title'),
            innerHTML: el.innerHTML?.substring(0, 100)
          });
        });
      }
    };

    // Run debug immediately
    debugGoogleAttribution();

    // Run debug after delays
    setTimeout(debugGoogleAttribution, 1000);
    setTimeout(debugGoogleAttribution, 3000);
    setTimeout(debugGoogleAttribution, 5000);


   
    setIsMapLoaded(true);

    // Polygons come from the stadium cache, not the football API. The API list
    // still drives hover popups and on-demand lookups; it no longer decides
    // what is green or clickable.
    const countryKeys = Object.keys(cachedStadiums?.countries || {});
    if (countryKeys.length > 0) {
      console.log(`🎨 Rendering ${countryKeys.length} countries on map...`);
      renderCountriesOnMap(map, countryKeys, gmaps);
    } else {
      console.warn('⚠️ Stadium cache empty at map init - no country polygons rendered');
    }
      })();
    };

  // Sets up country hover detection, highlighting, and info windows for the map
  const setupCountryHoverEffects = (map) => {
    let hoverTimeout = null;
    let currentInfoWindow = null;
    let currentPolygon = null;
    let isCreatingInfoWindow = false; // ADD: Global flag to prevent multiple creation
    
    // Create geocoder once and reuse
    const geocoder = new window.google.maps.Geocoder();
    
    console.log('🔧 ENHANCED: Setting up hover effects');
    console.log('📊 Countries data available:', countriesDataRef.current?.length || 0);
    console.log('📊 Sample countries:', countriesDataRef.current?.slice(0, 3)?.map(c => c.name));
    
    const closeInfoWindow = () => {
      if (currentInfoWindow && currentInfoWindow.customDiv) {
        if (currentInfoWindow.customDiv.parentNode) {
          currentInfoWindow.customDiv.parentNode.removeChild(currentInfoWindow.customDiv);
        }
        currentInfoWindow = null;
      }
      
      // Remove all custom divs as backup
      const customDivs = mapRef.current?.querySelectorAll('div[style*="position: absolute"][style*="z-index: 9999"]');
      if (customDivs) {
        customDivs.forEach(div => {
          if (div.parentNode) {
            div.parentNode.removeChild(div);
          }
        });
      }
    };
    
    // Function to highlight country
    const highlightCountry = (countryCode, countryName) => {
      console.log('🎨 Highlighting country:', countryName, countryCode);
      
      // Remove previous highlight
      if (currentPolygon) {
        currentPolygon.setMap(null);
        currentPolygon = null;
      }
      
      // Add professional delay
      setTimeout(() => {
        loadCountryGeoJson()
          .then(geoJsonData => {
            const countryFeature = geoJsonData.features.find(feature => {
              const featureCode = feature.properties.ISO_A2;
              const featureName = feature.properties.NAME_EN || feature.properties.NAME;
              
              return featureCode === countryCode || 
                    featureCode?.toLowerCase() === countryCode?.toLowerCase() ||
                    featureName?.toLowerCase() === countryName?.toLowerCase();
            });
            
            if (countryFeature) {
              console.log('✅ Found country boundaries for:', countryName);
              
              const coordinates = countryFeature.geometry.coordinates;
              
              if (countryFeature.geometry.type === 'Polygon') {
                const paths = coordinates[0].map(coord => ({
                  lat: coord[1],
                  lng: coord[0]
                }));
                
                currentPolygon = new window.google.maps.Polygon({
                  paths: paths,
                  strokeColor: '#16a34a',
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  fillColor: '#22c55e',
                  fillOpacity: 0.15,
                  map: map,
                  zIndex: 1000,
                  clickable: false
                });
              } else if (countryFeature.geometry.type === 'MultiPolygon') {
                const allPaths = [];
                coordinates.forEach(polygon => {
                  const paths = polygon[0].map(coord => ({
                    lat: coord[1],
                    lng: coord[0]
                  }));
                  allPaths.push(paths);
                });
                
                currentPolygon = new window.google.maps.Polygon({
                  paths: allPaths,
                  strokeColor: '#16a34a',
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  fillColor: '#22c55e',
                  fillOpacity: 0.15,
                  map: map,
                  zIndex: 1000,
                  clickable: false
                });
              }
            }
          })
          .catch(error => {
            console.log('❌ Failed to load country boundaries:', error);
          });
      }, 100);
    };
    
    const removeHighlight = () => {
      if (currentPolygon) {
        console.log('🧹 Removing country highlight');
        currentPolygon.setMap(null);
        currentPolygon = null;
      }
      map.setOptions({ draggableCursor: 'grab' });
    };
    
    // Add mousemove listener with proper event handling (only on world view)
    const mouseMoveListener = map.addListener('mousemove', (event) => {
      // Only show hover popups on world view (zoom <= 4)
      const currentZoom = map.getZoom();
      if (currentZoom > 4) {
        return; // Skip hover effects when zoomed in
      }
      // Clear existing timeout
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = null;
      }
      
      // Set new timeout for hover action
      hoverTimeout = setTimeout(async () => {
        // Close any existing info window at start of new hover
        closeInfoWindow();
        removeHighlight();
        
        // Check if we have countries data
        const currentData = countriesDataRef.current;
        if (!currentData || currentData.length === 0) {
          console.log('⛔ HOVER BLOCKED: No countries data available');
          return;
        }
        
        console.log('🎯 HOVER ATTEMPT at:', event.latLng.toString());
        
        try {
          // Geocode the hover position
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          const rev = await fgReverseGeocode(lat, lng);

          if (!rev) {
            // No country found - probably ocean
            return;
          }

          let detectedCountryName = rev.countryName;
          let detectedCountryCode = googleToFootballCode(rev.countryCode);
          console.log('✅ Reverse-geocode:', rev.countryName, rev.countryCode, '→', detectedCountryCode);


          if (detectedCountryName && detectedCountryCode) {
            console.log('🌍 DETECTED COUNTRY:', detectedCountryName, `(${detectedCountryCode})`);
            
            // Highlight the country
            highlightCountry(detectedCountryCode, detectedCountryName);
            
            // Match country data or fetch on-demand
            let matchingCountry = currentData.find(country => 
              country.code?.toLowerCase() === detectedCountryCode.toLowerCase()
            );
            
            // If country not in cache, fetch on-demand
            if (!matchingCountry) {
              console.log('🔄 LAZY LOAD: Fetching data on-demand for', detectedCountryName);
              const onDemandData = await fetchCountryOnDemand(detectedCountryCode, detectedCountryName);
              if (onDemandData) {
                matchingCountry = {
                  name: detectedCountryName,
                  code: detectedCountryCode,
                  ...onDemandData // Contains flag, stadiums, topLeagues, competitions, etc.
                };
              }
            }
            
            // Ensure no existing windows before creating new one
            closeInfoWindow();
            
            // Prepare country info
            const countryInfo = matchingCountry || {
              name: detectedCountryName,
              code: detectedCountryCode,
              stadiums: 0,
              topLeagues: ['Data loading...'],
              continent: 'Unknown',
              flag: '🏳️'
            };
            
            // Create custom popup instead of Google Maps InfoWindow
            const mapContainer = mapRef.current;
            const bounds = googleMapRef.current.getBounds();
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const mapWidth = mapContainer.offsetWidth;
            const mapHeight = mapContainer.offsetHeight;

            const lat = event.latLng.lat();
            const lng = event.latLng.lng();

            // Calculate pixel position
            const x = ((lng - sw.lng()) / (ne.lng() - sw.lng())) * mapWidth;
            const y = ((ne.lat() - lat) / (ne.lat() - sw.lat())) * mapHeight;

            // Create custom popup div
            const customDiv = document.createElement('div');
            customDiv.style.position = 'absolute';
            customDiv.style.zIndex = '9999';
            customDiv.style.pointerEvents = 'none'; // CHANGED: Allow clicks to pass through

            // Smart positioning to avoid blocking the country - Enhanced
            const countryBuffer = 80; // Larger buffer zone
            let left, top;

            // More aggressive positioning away from country center

            if (x < mapWidth / 3) {
              // If hovering left third, put popup on right
              left = x + countryBuffer;
            } else if (x > (mapWidth * 2) / 3) {
              // If hovering right third, put popup on left
              left = x - 200 - countryBuffer;
            } else {
              // If hovering center, put popup at edge
              left = x + countryBuffer;
              if (left + 200 > mapWidth) {
                left = x - 200 - countryBuffer;
              }
            }

            // Vertical positioning - always try to avoid center
            if (y < mapHeight / 2) {
              // If top half, put popup below
              top = y + countryBuffer;
            } else {
              // If bottom half, put popup above
              top = y - 120 - countryBuffer;
            }

            // Ensure popup stays within bounds
            left = Math.max(10, Math.min(left, mapWidth - 250));
            top = Math.max(10, Math.min(top, mapHeight - 170));

            // Apply the calculated position to the popup div
            customDiv.style.left = `${left}px`;
            customDiv.style.top = `${top}px`;
            customDiv.innerHTML = createHoverInfoContent(countryInfo);

            mapContainer.appendChild(customDiv);

            customDiv.style.left = `${left}px`;
            customDiv.style.top = `${top}px`;
            customDiv.style.transform = 'translateX(-50%)';

            customDiv.innerHTML = createHoverInfoContent(countryInfo);

            mapContainer.appendChild(customDiv);

            // Store reference for cleanup
            currentInfoWindow = { customDiv: customDiv, close: () => {} };

            // FASTER AUTO-HIDE: Remove popup after 1.2 seconds to prevent blocking
            setTimeout(() => {
              if (currentInfoWindow && currentInfoWindow.customDiv === customDiv) {
                closeInfoWindow();
                removeHighlight();
              }
            }, 1200); // Faster auto-hide

            // IMMEDIATE HIDE on mouse movement - more responsive
            let hideOnMoveListener = null;
            const hideOnMove = () => {
              if (currentInfoWindow && currentInfoWindow.customDiv === customDiv) {
                closeInfoWindow();
                removeHighlight();
                if (hideOnMoveListener) {
                  window.google.maps.event.removeListener(hideOnMoveListener); // ✅ CORRECT: Google Maps method
                  hideOnMoveListener = null;
                }
              }
            };
            setTimeout(() => {
              hideOnMoveListener = map.addListener('mousemove', hideOnMove); // ✅ CORRECT: Google Maps method
            }, 1500);

            map.setOptions({ draggableCursor: 'pointer' });
          } else {
            console.log('⛔ No country detected');
            removeHighlight();
          }
        } catch (error) {
          console.log('⛔ Hover geocoding failed:', error);
          removeHighlight();
        }
      }, 300);
    });
    
    console.log('✅ Mousemove listener attached:', mouseMoveListener);
    
    console.log('✅ Mousemove listener attached:', mouseMoveListener);
  
    // Add zoom listener to clean up hover effects when zooming in
    map.addListener('zoom_changed', () => {
      const currentZoom = map.getZoom();
      if (currentZoom > 4) {
        // Clean up hover effects when zoomed in
        closeInfoWindow();
        removeHighlight();
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
        }
      }
    });

    // Enhanced click handler for country selection and stadium loading
    map.addListener('click', async (event) => {
      console.log('🗺️ Map clicked, processing...');

      // CHECK: Ignore clicks on stadium markers or UI elements
      if (event.domEvent && event.domEvent.target) {
        const target = event.domEvent.target;
        // Skip if clicking on stadium markers, controls, or UI elements
        if (target.closest('.gm-style') || 
            target.closest('[role="button"]') || 
            target.closest('.gm-control-active')) {
          console.log('🚫 CLICK IGNORED: UI element clicked');
          return;
        }
      }
      
      // Only reset on intentional country changes, not accidental clicks
      if (selectedCountry) {
        console.log('🚫 CLICK IGNORED: Country already selected, use reset button');
        return;
      }

      // DEBOUNCE: Prevent duplicate clicks
      if (window.clickProcessing) {
        console.log('🔄 Click already processing, ignoring duplicate');
        return;
      }
      window.clickProcessing = true;

      // Clear flag after processing
      setTimeout(() => {
        window.clickProcessing = false;
      }, 2000);

    // EMERGENCY: Force clear all popups immediately on any click
    const emergencyCloseAll = () => {
      const allCustomDivs = document.querySelectorAll('div[style*="position: absolute"][style*="z-index: 9999"]');
      allCustomDivs.forEach(div => {
        if (div.parentNode) {
          div.parentNode.removeChild(div);
        }
      });
    };

    emergencyCloseAll();
      
      // PRIORITY: Always close hover elements first
      closeInfoWindow();
      removeHighlight();
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = null;
      }
      
      // Small delay to ensure hover elements are cleared
      await new Promise(resolve => setTimeout(resolve, 50));

      // Check if we clicked on a country
      try {
        // inside your mousemove or click handler that receives (event)
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        const rev = await fgReverseGeocode(lat, lng);

        if (!rev) {
          console.log('ℹ️ No country clicked');
          return;
        }

        let detectedCountryName = rev.countryName;
        let detectedCountryCode = googleToFootballCode(rev.countryCode);
        console.log('Reverse-geocode:', rev.countryName, rev.countryCode, '→', detectedCountryCode);


        if (detectedCountryName && detectedCountryCode) {
          console.log(`🎯 COUNTRY SELECTED: ${detectedCountryName}`);
          
          // Find country data
          let countryData = countriesDataRef.current.find(country => 
            country.code?.toLowerCase() === detectedCountryCode.toLowerCase()
          );

          // 🔥 NEW: If country not in priority list, fetch data on-demand
          if (!countryData) {
            console.log(`🔄 LAZY COUNTRY: ${detectedCountryName} not in priority list, fetching on-demand...`);
            const onDemandData = await fetchCountryOnDemand(detectedCountryCode, detectedCountryName);
            
            if (onDemandData) {
              countryData = {
                name: detectedCountryName,
                code: detectedCountryCode,
                center: { lat: event.latLng.lat(), lng: event.latLng.lng() },
                ...onDemandData // Contains stadiums, topLeagues, area, etc.
              };
            }
          }

          if (countryData) {
            setSelectedCountry(countryData);
            
            // Simple zoom to country
            map.panTo(countryData.center || { lat: event.latLng.lat(), lng: event.latLng.lng() });

            // Polygons now carry `countryKey` (the exact premium key) and hold
            // every ring as a separate path, so match on the normalised key and
            // walk getPaths(). Kept rather than deleted: this is the ONLY zoom on
            // this path - displayStadiumPins moves no camera, and the fitBounds
            // over markers belongs to clickCountryFromPopup, not here. Reachable
            // only for countries with no polygon, since a clickable polygon
            // swallows the map click.
            const clickedKey = normaliseCountry(detectedCountryName);
            const countryPolys = (window.countryPolygons || [])
              .filter(p => normaliseCountry(p.countryKey) === clickedKey);
            if (countryPolys.length > 0) {
              const bounds = new window.google.maps.LatLngBounds();
              countryPolys.forEach(p =>
                p.getPaths().forEach(path => path.forEach(pt => bounds.extend(pt)))
              );
              map.fitBounds(bounds);
            }

            
            // Load and display stadium pins - Enhanced with better error handling
            console.log(`🏟️ Starting stadium loading for ${countryData.name} (${detectedCountryCode})`);
            console.log(`🏟️ CALLING fetchStadiumsForCountry(${detectedCountryCode}, ${detectedCountryName})`);

            try {
              // Add loading state
              setIsLoadingStadiums(true);
              
              const stadiums = await fetchStadiumsForCountry(detectedCountryCode, detectedCountryName);
              console.log(`🏟️ STADIUM RESULT: ${stadiums?.length || 0} stadiums returned`);
              console.log(`🏟️ Received ${stadiums.length} stadiums for ${detectedCountryName}`);

              if (stadiums && stadiums.length > 0) {
                console.log(`🏟️ PROCESSING: ${stadiums.length} stadiums for display`);
                
                // Update country data with DISPLAYED stadium count
                const displayedCount = stadiums.length;
                const updatedCountryData = {
                  ...countryData,
                  stadiums: displayedCount,
                  realStadiumCount: displayedCount,
                  currentView: 'all-leagues'
                };
                setSelectedCountry(updatedCountryData);
                
                console.log(`📊 STADIUM COUNT SYNC: ${displayedCount} stadiums loaded and displayed`);

                // Load standings for the top competition
                if (updatedCountryData.topCompetition?.id) {
                  console.log('📊 Loading standings for:', updatedCountryData.topCompetition.name);
                  try {
                    const standingsData = await fgFootballStandings(updatedCountryData.topCompetition.id);
                    setStandings(standingsData.standings);
                    console.log('✅ Standings loaded:', standingsData.standings?.length);
                  } catch (error) {
                    console.error('❌ Failed to load standings:', error);
                    setStandings(null);
                  }
                }
                
                displayStadiumPins(stadiums, map);
                setStadiumPins(stadiums);
                console.log(`✅ STADIUM PINS: ${stadiums.length} pins displayed for ${detectedCountryName}`);
              } else {
                console.log(`❌ NO STADIUMS: Zero stadiums to display for ${detectedCountryName}`);
                console.log(`❌ STADIUM DATA:`, stadiums);
                console.warn(`⚠️ NO STADIUMS: Zero stadiums returned for ${detectedCountryName} (${detectedCountryCode})`);
                
                // API-DRIVEN RETRY: Get alternative country names from REST Countries API
                const alternativeNames = await getCountryAlternativeNames(detectedCountryCode);
                
                for (const altName of alternativeNames) {
                  console.log(`🔄 RETRY: Attempting stadium fetch with "${altName}"`);
                  const alternativeStadiums = await fetchStadiumsForCountry(detectedCountryCode, altName);
                  
                  if (alternativeStadiums && alternativeStadiums.length > 0) {
                    console.log(`🏟️ RETRY PROCESSING: ${alternativeStadiums.length} stadiums for display`);
                    displayStadiumPins(alternativeStadiums, map);
                    setStadiumPins(alternativeStadiums);
                    console.log(`✅ ALTERNATIVE SUCCESS: ${alternativeStadiums.length} stadiums found using "${altName}"`);
                    break; // Stop on first success
                  }
                }
              }
            } catch (error) {
              console.error(`❌ STADIUM LOADING FAILED for ${detectedCountryName}:`, error);
            } finally {
              setIsLoadingStadiums(false);
            }
          }
        }
      } catch (error) {
        console.log('❌ Click geocoding failed:', error);
      }

      map.setOptions({ draggableCursor: 'grab' });
    });
  };

  // NEW FUNCTION: Render all countries on initial map load
  // gmaps is the google.maps namespace captured in initializeMap alongside the
  // Map constructor. It is passed in rather than read off window because the
  // GeoJSON await below is long enough for a second loader to swap the global,
  // which is what made every Polygon reject this Map with "not an instance of Map".
  const renderCountriesOnMap = async (map, countryKeys, gmaps) => {
    console.log(`🎨 RENDERING: ${countryKeys.length} countries on map`);

    try {
      const geoJsonData = await loadCountryGeoJson();
      const { byName, byAdmin } = buildFeatureIndexes(geoJsonData);

      window.countryPolygons = [];
      const unmatched = [];

      countryKeys.forEach(countryKey => {
        const idxs = resolveCountryFeatures(countryKey, byName, byAdmin);
        if (idxs.length === 0) {
          unmatched.push(countryKey);
          return;
        }

        // EVERY ring of every resolved feature becomes a path on ONE polygon.
        // The old code took coordinates[0] only, which discarded interior rings.
        // That matters here: Lesotho and San-Marino are both premium keys and
        // both are enclaves. Without their holes punched out, South-Africa's and
        // Italy's fills cover them and they can never be clicked.
        const paths = [];
        idxs.forEach(i => {
          const g = geoJsonData.features[i].geometry;
          if (!g) return;
          const polys = g.type === 'Polygon' ? [g.coordinates]
                      : g.type === 'MultiPolygon' ? g.coordinates
                      : [];
          polys.forEach(rings => rings.forEach(ring => {
            paths.push(ring.map(coord => ({ lat: coord[1], lng: coord[0] })));
          }));
        });

        if (paths.length === 0) {
          unmatched.push(countryKey);
          return;
        }

        // One Polygon per country, not one per ring: ~133 objects instead of
        // several hundred, and hover restyling is a single setOptions call.
        const polygon = new gmaps.Polygon({
          paths: paths,
          strokeColor: '#22c55e',
          strokeOpacity: 0.6,
          strokeWeight: 1,
          fillColor: '#22c55e',
          fillOpacity: 0.2,
          map: map,
          zIndex: 100,
          clickable: true
        });

        polygon.countryKey = countryKey;
        window.countryPolygons.push(polygon);

        polygon.addListener('click', () => {
          console.log(`🎯 POLYGON CLICKED: ${countryKey}`);
          window.clickCountryFromPopup(countryKey);
        });

        polygon.addListener('mouseover', () => {
          polygon.setOptions({
            fillOpacity: 0.4,
            strokeWeight: 2
          });
          map.setOptions({ draggableCursor: 'pointer' });
        });

        polygon.addListener('mouseout', () => {
          polygon.setOptions({
            fillOpacity: 0.2,
            strokeWeight: 1
          });
          map.setOptions({ draggableCursor: 'grab' });
        });
      });

      console.log(`🎨 RENDER COMPLETE: ${window.countryPolygons.length} polygons, ${unmatched.length} unmatched`, unmatched);

    } catch (error) {
      console.error('❌ Failed to render countries:', error);
    }
  };

    const fetchCountryOnDemand = async (countryCode, countryName = null) => {
      const cacheKey = `country_${countryCode}`;
      if (apiCacheRef.current.countryDetails[cacheKey]) {
        console.log(`📦 CACHE HIT: Using cached data for ${countryName}`);
        return apiCacheRef.current.countryDetails[cacheKey];
      }
      
      console.log(`🔄 LAZY LOAD: Fetching data for ${countryName} on-demand`);
      try {
        // Get competitions data (already loaded)
        const country = countriesDataRef.current.find(c => c.code === countryCode);
        const competitions = country?.competitions || [];

        const countryData = {
          name: countryName || countryCode,
          code: countryCode,
          flag: country?.flag || null, // football-data.org's area.flag; no flag URL means render nothing
          stadiums: competitions.length * 10, // Estimate
          topLeagues: competitions.slice(0, 2).map(c => c.name),
          hasWomensLeagues: false, // Football-Data.org doesn't have women's data
          competitions: competitions,
          cachedAt: Date.now()
        };

        apiCacheRef.current.countryDetails[`country_${countryCode}`] = countryData;
        console.log(`💾 CACHED: ${countryName} data saved for future use`);

        return countryData;
      } catch (error) {
        return null;
      }
    };

        const getCountryHistoricalFact = (countryCode) => {
          const facts = {
            'GB': '🏴󠁧󠁢󠁥󠁮󠁧󠁿 Birthplace of modern football (1863)',
            'BR': '🏆 5-time World Cup champions',
            'DE': '🏆 4-time World Cup winners',
            'AR': '🏆 3-time World Cup champions',
            'IT': '🏆 4-time World Cup winners',
            'FR': '🏆 2-time World Cup champions',
            'ES': '🏆 2010 World Cup champions',
            'NL': '🧡 Total Football pioneers',
            'PT': '🏆 Euro 2016 champions',
            'US': '🏆 4-time Women\'s World Cup champions',
            'JP': '🏆 2011 Women\'s World Cup champions',
            'AU': '🏆 4-time AFC Asian Cup winners',
            'MX': '🏆 11-time CONCACAF champions',
            'EG': '🏆 7-time African Cup of Nations',
            'NG': '🏆 3-time African Cup of Nations',
            'IN': '🏆 1951 Asian Games football gold',
            'CN': '🏆 1984 AFC Asian Cup champions',
            'RU': '🏆 1960 European Championship winners',
            'SE': '🏆 1948 Olympic football champions',
            'NO': '🏆 2000 European Championship runners-up'
        };
        
        const fact = facts[countryCode];
          return fact ? `
            <div style="margin-top: 6px; font-size: 10px; color: #6b7280; text-align: center; font-style: italic;">
              ${fact}
            </div>
          ` : '';
        };
  
  const createHoverInfoContent = (country) => {
    const stadiumText = country.stadiums > 0 
      ? `${country.stadiums} stadium${country.stadiums !== 1 ? 's' : ''}`
      : 'Football venues';
      
    return `
      <div class="info-window-enhanced" style="
        padding: 8px; 
        min-width: 180px; 
        max-width: 200px;
        font-family: 'Inter', 'Segoe UI', Arial, sans-serif; 
        animation: fadeInUp 0.2s ease-out;
        background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
        border-radius: 8px;
        box-shadow: 0 2px 12px rgba(0,0,0,0.15);
        border: 1px solid rgba(34, 197, 94, 0.2);
        pointer-events: none;
        position: relative;
      ">
        <!-- Compact Header -->
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;">
          <h3 style="margin: 0; color: #1f2937; font-size: 14px; font-weight: 700;">
            ${country.name}
          </h3>
          ${country.flag ? `<span style="font-size: 16px;">${country.flag}</span>` : ''}
        </div>
        
        <!-- Compact Stadium Count -->
        <div style="display: flex; align-items: center; margin: 4px 0;">
          <span style="font-size: 16px; font-weight: 800; color: #16a34a; margin-right: 6px;">
            ⚽ ${country.stadiums}
          </span>
          <span style="color: #374151; font-size: 11px;">
            ${stadiumText}
          </span>
        </div>
        
        <!-- Simple Click Instruction -->
        <div style="
          margin-top: 6px; 
          padding: 4px 6px; 
          background: rgba(59, 130, 246, 0.1); 
          border-radius: 4px;
          text-align: center;
          font-size: 10px;
          color: #3b82f6;
          font-weight: 500;
        ">
          Click country to explore stadiums
        </div>
      </div>
    `;
  };

  // NEW FUNCTION: Display stadium pins on the map
  const displayStadiumPins = (stadiums, map) => {
    console.log(`🏟️ DISPLAYING: ${stadiums.length} stadium pins`);

    console.log(`🏟️ DISPLAYING: ${stadiums.length} stadium pins`);
    console.log('📍 Stadium data sample:', stadiums.slice(0, 2).map(s => ({
      name: s.name,
      hasCoordinates: !!s.coordinates,
      coordinates: s.coordinates
    })));
    
    // Clear existing pins
    clearAllStadiumMarkers();

    stadiums.forEach((stadium, index) => {
      if (!stadium.coordinates) return;

      // Create custom stadium marker
      const marker = new window.google.maps.Marker({
        position: stadium.coordinates,
        map: map,
        title: `${stadium.name} - ${stadium.team}`,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="36" height="48" viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              <!-- Outer glow -->
              <circle cx="18" cy="16" r="14" fill="#22c55e" opacity="0.3" filter="url(#glow)"/>
              
              <!-- Main pin shape -->
              <path d="M 18 2 C 10.268 2 4 8.268 4 16 C 4 24 18 44 18 44 C 18 44 32 24 32 16 C 32 8.268 25.732 2 18 2 Z" 
                    fill="url(#gradient)" 
                    stroke="#ffffff" 
                    stroke-width="2.5"
                    filter="url(#glow)"/>
              
              <!-- Gradient definition -->
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#22c55e;stop-opacity:1" />
                  <stop offset="100%" style="stop-color:#16a34a;stop-opacity:1" />
                </linearGradient>
              </defs>
              
              <!-- Inner stadium icon -->
              <g transform="translate(18, 16)">
                <!-- Stadium structure -->
                <ellipse cx="0" cy="0" rx="7" ry="5" fill="#ffffff" opacity="0.9"/>
                <ellipse cx="0" cy="0" rx="5" ry="3" fill="#22c55e"/>
                
                <!-- Field lines -->
                <line x1="-5" y1="0" x2="5" y2="0" stroke="#ffffff" stroke-width="0.5"/>
                <circle cx="0" cy="0" r="1.5" fill="none" stroke="#ffffff" stroke-width="0.5"/>
              </g>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(36, 48),
          anchor: new window.google.maps.Point(18, 44)
        },
        animation: window.google.maps.Animation.DROP,
        zIndex: 1000 + index
      });

      

      // Add click listener for stadium details
      marker.addListener('click', () => {
        setSelectedStadium(stadium);

        // Under 768px, use our own bottom-sheet overlay instead of Google's InfoWindow
        if (window.innerWidth < 768) {
          setMobileStadiumCard(stadium);
          return;
        }

        // Create stadium info window
        const infoWindow = new window.google.maps.InfoWindow({
          content: createProfessionalStadiumPopup(stadium),
          maxWidth: 350
        });

        // Close other info windows
        if (window.currentStadiumInfoWindow) {
          window.currentStadiumInfoWindow.close();
        }

        infoWindow.open(map, marker);
        window.currentStadiumInfoWindow = infoWindow;
      });

      window.currentStadiumMarkers.push(marker);
    });

    console.log(`✅ DISPLAYED: ${window.currentStadiumMarkers.length} stadium markers`);
  };

  // NEW FUNCTION: Create stadium info window content
  // PROFESSIONAL 10/10 STADIUM POPUP
  const createProfessionalStadiumPopup = (stadium) => {
    const teamName = stadium.team || stadium.teamName || 'Unknown Team';
    const stadiumName = stadium.name || stadium.venue || 'Unknown Stadium';
    // stadiums-premium.json carries no address field, and the upstream one
    // is sometimes a registered office rather than the ground. The
    // coordinates went through the acceptance rule, so they are what to
    // trust: send the viewer to the pin itself.
    const lat = stadium.latitude;
    const lng = stadium.longitude;
    const directionsUrl = (lat != null && lng != null)
      ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
      : '';
    const league = stadium.leagueName || stadium.league || '';

    // Get team crest from cache data
    const teamCrest = stadium.crestUrl || stadium.teamLogo || '';
    const clubColors = stadium.clubColors || '';
    const founded = stadium.founded || '';
    const city = stadium.city || '';
    // 0 means "not known", not "holds nobody" - render only when truthy.
    const capacityLine = stadium.capacity
      ? `👥 ${stadium.capacity.toLocaleString()}`
      : '';

    // Context-strip segments, in display order. The separator belongs to the
    // segment that follows it rather than being hardcoded per line, so an
    // absent earlier segment can never leave a dangling leading "· ".
    // Each surviving segment still renders as its own <span>, exactly as
    // before, so the flex gap between them is unchanged.
    const stripHtml = [
      city ? `📍 ${city}` : '',
      capacityLine,
      founded ? `📅 ${founded}` : '',
      clubColors ? `👕 ${clubColors}` : '',
      lat != null && lng != null
        ? `<a href="https://www.windy.com/?${lat},${lng},11" target="_blank" rel="noopener noreferrer" style="color: #0284c7; text-decoration: none; font-weight: 600;">🌤️ Check forecast</a>`
        : ''
    ]
      .filter(Boolean)
      .map((segment, i) => `<span>${i === 0 ? '' : '· '}${segment}</span>`)
      .join('');
    const travelLinks = city ? buildTravelLinks(city, stadium.country || '') : null;

    // Match-day fixture (if any) for this team, stashed on window when the
    // fixtures fetch resolved. Absent/missing map just means no fixture line.
    const fixture = window.currentMatchDayByTeamId?.get(stadium.id) || null;
    const fixtureLine = fixture
      ? `⚽ vs ${fixture.opponent || 'TBD'} — ${new Date(fixture.utcDate).toLocaleString(undefined, {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        })}`
      : '';

    return `
      <style>
        .stadium-popup-card { width: 320px; }
        .stadium-popup-card .popup-header { padding: 16px; }
        .stadium-popup-card .popup-strip { padding: 8px 16px; font-size: 12px; gap: 4px; }
        .stadium-popup-card .popup-travel { padding: 16px 16px 0; }
        .stadium-popup-card .popup-travel-buttons { gap: 10px; }
        .stadium-popup-card .popup-travel-btn { padding: 10px; font-size: 13px; }
        .stadium-popup-card .popup-footer { padding: 16px; }

        @media (max-width: 390px) {
          .stadium-popup-card { width: 260px; }
          .stadium-popup-card .popup-header { padding: 12px; }
          .stadium-popup-card .popup-strip { padding: 6px 12px; font-size: 11px; gap: 3px; }
          .stadium-popup-card .popup-travel { padding: 12px 12px 0; }
          .stadium-popup-card .popup-travel-buttons { gap: 8px; }
          .stadium-popup-card .popup-travel-btn { padding: 8px 6px; font-size: 12px; }
          .stadium-popup-card .popup-footer { padding: 12px; }
        }
      </style>
      <div class="stadium-popup-card" style="
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
        background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      ">
        <!-- Header with Team Colors -->
        <div class="popup-header" style="
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          position: relative;
          overflow: hidden;
        ">
          <!-- Decorative football pattern -->
          <div style="
            position: absolute;
            top: -20px;
            right: -20px;
            width: 80px;
            height: 80px;
            opacity: 0.15;
            font-size: 60px;
          ">⚽</div>
          
          <div style="display: flex; align-items: center; gap: 12px; position: relative;">
            ${teamCrest ? `
              <img 
                src="${teamCrest}" 
                alt="${teamName}" 
                style="
                  width: 48px; 
                  height: 48px; 
                  border-radius: 8px; 
                  background: white;
                  padding: 4px;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                "
                onerror="this.style.display='none'"
              />
            ` : ''}
            
            <div style="flex: 1;">
              <h3 style="
                margin: 0 0 4px 0; 
                color: white; 
                font-size: 16px; 
                font-weight: 700;
                text-shadow: 0 1px 2px rgba(0,0,0,0.2);
              ">${stadiumName}</h3>
              <p style="
                margin: 0;
                color: rgba(255,255,255,0.95);
                font-size: 13px;
                font-weight: 500;
              ">${teamName}${league ? ` · ${league}` : ''}</p>
            </div>
          </div>
        </div>

        <!-- Context Strip -->
        <div class="popup-strip" style="
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          color: #6b7280;
          background: #fafafa;
          border-bottom: 1px solid #f0f0f0;
        ">
          ${stripHtml}
        </div>

        <!-- Next match (match-day highlight) -->
        ${fixtureLine ? `
          <div class="popup-next-match" style="
            margin: 12px 16px 0;
            padding: 8px 12px;
            background: rgba(245, 158, 11, 0.12);
            border: 1px solid rgba(245, 158, 11, 0.35);
            border-radius: 8px;
            color: #b45309;
            font-size: 12px;
            font-weight: 700;
          ">${fixtureLine}</div>
        ` : ''}

        <!-- Travel -->
        ${travelLinks ? `
          <div class="popup-travel">
            <div style="
              font-size: 10px;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              color: #9ca3af;
              margin-bottom: 8px;
            ">
              Plan a trip
            </div>
            <div class="popup-travel-buttons" style="display: flex;">
              <a href="${travelLinks.flights}" target="_blank" rel="noopener noreferrer" class="popup-travel-btn" style="
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                min-height: 44px;
                background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
                color: #ffffff;
                font-weight: 700;
                border-radius: 8px;
                text-decoration: none;
                box-sizing: border-box;
              ">
                ✈️ Flights
              </a>

              <a href="${travelLinks.hotels}" target="_blank" rel="noopener noreferrer" class="popup-travel-btn" style="
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                min-height: 44px;
                background: linear-gradient(135deg, #ea580c 0%, #c2410c 100%);
                color: #ffffff;
                font-weight: 700;
                border-radius: 8px;
                text-decoration: none;
                box-sizing: border-box;
              ">
                🏨 Hotels
              </a>
            </div>
          </div>
        ` : ''}

        <!-- Footer -->
        <div class="popup-footer">
          ${directionsUrl ? `
            <a href="${directionsUrl}" target="_blank" rel="noopener noreferrer" style="
              display: block;
              margin: 0 0 10px 0;
              font-size: 12px;
              color: #1B6B52;
              font-weight: 600;
              text-decoration: none;
            ">📍 Get directions</a>
          ` : ''}

          <div style="
            background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
            padding: 8px 12px;
            border-radius: 8px;
            text-align: center;
            border: 1px solid #86efac;
          ">
            <div style="
              color: #1B6B52;
              font-size: 11px;
              font-weight: 700;
            ">
              📍 VeylorCraft Away - Find live sport you'd travel for
            </div>
          </div>
        </div>
      </div>
    `;
  };

  const resetMap = () => {
    console.log('🌍 RESET: Returning to world view');
    
    // Reset all state
    setSelectedCountry(null);
    setSelectedStadium(null);
    setStandings(null);
    setIsLoading(false);
    setStadiumPins([]);
    setAppMode('world');
    setViewState({
      mode: 'world',
      center: { lat: 20, lng: 0 },
      zoom: 2,
      country: null,
      stadium: null
    });
    
    // Clear stadium markers
    if (window.currentStadiumMarkers) {
      window.currentStadiumMarkers.forEach(marker => marker.setMap(null));
      window.currentStadiumMarkers = [];
    }
    
    // Close info windows
    if (window.currentStadiumInfoWindow) {
      window.currentStadiumInfoWindow.close();
    }
    
    // Smart zoom back to world
    if (googleMapRef.current) {
      // Smooth transition back to world view
      googleMapRef.current.panTo({ lat: 20, lng: 0 });
      
      // Progressive zoom out for better UX
      const currentZoom = googleMapRef.current.getZoom();
      if (currentZoom > 4) {
        // Animate zoom out in steps
        const zoomOut = () => {
          const zoom = googleMapRef.current.getZoom();
          if (zoom > 2) {
            googleMapRef.current.setZoom(zoom - 1);
            setTimeout(zoomOut, 200);
          }
        };
        zoomOut();
      } else {
        googleMapRef.current.setZoom(2);
      }
    }
  };

  // Load Google Maps API
  useEffect(() => {
    let cancelled = false;
    loadGoogleMapsOnce()
      .then(() => {
        if (cancelled) return;
        console.log('🔍 Google Maps API loaded successfully');
        console.log('📊 Countries data length at API load:', countriesData.length);
        // Set only now: the Map constructor is verified to exist.
        setGoogleMapsReady(true);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('❌ Failed to load Google Maps API:', error);
      });
    return () => { cancelled = true; };
  }, []);

  // Initialize map when BOTH Google Maps AND countries data are ready
  useEffect(() => {
    console.log('🎯 Checking initialization conditions:');
    console.log('   - googleMapsReady:', googleMapsReady);
    console.log('   - window.google:', !!window.google);
    console.log('   - mapRef.current:', !!mapRef.current);
    console.log('   - countriesData.length:', countriesData.length);
    console.log('   - isMapLoaded:', isMapLoaded);
    console.log('   - isLoadingCountries:', isLoadingCountries);

    if (googleMapsReady && mapRef.current && cacheLoaded && cachedStadiums?.countries && !isMapLoaded) {
      console.log('🚀 ALL CONDITIONS MET - Initializing map with countries data...');
      // googleMapsReady already means "Map constructor verified", so there is
      // nothing left to ensure. Calling a second loader here is what injected
      // the duplicate tag.
      initializeMap();


    }
    // Cleanup function
    return () => {
      if (googleMapRef.current) {
        window.google?.maps?.event?.clearListeners(googleMapRef.current, 'mousemove');
      }
    };
  }, [googleMapsReady, cacheLoaded, cachedStadiums, isMapLoaded]);

  const headerStats = useMemo(() => {
    const countries = cachedStadiums?.countries;
    if (!countries) return null;
    const seen = new Set();
    let leagueCount = 0;
    for (const country of Object.values(countries)) {
      for (const league of country.leagues || []) {
        leagueCount += 1;
        for (const stadium of league.stadiums || []) {
          seen.add(`${stadium.venue}|${stadium.latitude}|${stadium.longitude}`);
        }
      }
    }
    return { venues: seen.size, countries: Object.keys(countries).length, leagues: leagueCount };
  }, [cachedStadiums]);

  /**
   * Distinct venues in the selected country across ALL of its leagues - the
   * number the "All leagues" option would actually put on the map.
   *
   * The key is the one the 'all' branch of the league dropdown dedupes on
   * (venue|latitude|longitude), so the label cannot promise a count that
   * selecting it fails to produce. stadiumPins.length is NOT usable here: it
   * is whatever is on the map right now, so picking the Championship made the
   * option read "All leagues (24 stadiums)".
   *
   * Memoised because it walks every league and every stadium in the country,
   * and the sidebar re-renders on hover, selection and pin changes that leave
   * both inputs untouched. Falls back to stadiumPins.length when the country
   * has no cache entry, so the label degrades to the old number instead of
   * claiming zero.
   */
  const allLeaguesVenueCount = useMemo(() => {
    const leagues = cachedStadiums?.countries?.[selectedCountry]?.leagues;
    if (!leagues) return null;
    const seen = new Set();
    for (const league of leagues) {
      for (const stadium of league.stadiums || []) {
        seen.add(`${stadium.venue}|${stadium.latitude}|${stadium.longitude}`);
      }
    }
    return seen.size;
  }, [selectedCountry, cachedStadiums]);

  return (
    <div className="premium-container relative overflow-hidden">
      
      {/* Football Field Background */}
      <div className="football-field-enhanced absolute inset-0">
        <div className="absolute inset-8 border-4 border-white rounded-lg">
          <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-white transform -translate-x-1/2"></div>
          <div className="absolute left-1/2 top-1/2 w-20 h-20 border-2 border-white rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
        </div>
      </div>

      {/* Premium Header */}
      <header className="premium-header" style={{ position: 'sticky', top: 0, zIndex: 50, padding: '1rem 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
              <img
                src="/away-mark.png"
                alt=""
                aria-hidden="true"
                className="header-logo"
                style={{ width: '52px', height: '52px', flexShrink: 0 }}
              />
              <div>
                <h1 style={{ margin: 0, lineHeight: 1.05 }}>
                  <span className="header-wordmark-kicker" style={{
                    display: 'block',
                    fontSize: '0.8125rem',
                    fontWeight: '400',
                    letterSpacing: '0.24em',
                    color: 'rgba(255,255,255,0.72)'
                  }}>
                    VEYLORCRAFT
                  </span>
                  <span className="header-wordmark" style={{
                    display: 'block',
                    fontSize: '2.125rem',
                    fontWeight: '800',
                    letterSpacing: '-0.01em',
                    color: '#ffffff'
                  }}>
                    AWAY
                  </span>
                </h1>
                <p className="header-tagline" style={{
                  margin: '0.4rem 0 0',
                  fontSize: '1rem',
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: '500'
                }}>
                  Find live sport you'd travel for.
                </p>
              </div>
            </div>
            <AboutData />
          </div>
          
          {selectedCountry && (
            <button
              onClick={resetMap}
              style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                backdropFilter: 'blur(8px)',
                transition: 'all 0.2s'
              }}
            >
              🌍 World View
            </button>
          )}
        </div>
        
        {headerStats && (
          <div className="header-badges" style={{
            marginTop: '0.875rem',
            fontSize: '0.875rem',
            fontWeight: '500',
            color: 'rgba(255,255,255,0.6)'
          }}>
            {headerStats.venues.toLocaleString()} venues · {headerStats.countries} countries · {headerStats.leagues} divisions
          </div>
        )}
        
        {/* Journey Mode Selector - Only show in world view */}
        {!selectedCountry && (
          <div className="header-journey" style={{ marginTop: '1rem' }}>
            <select 
              value={currentJourney?.id || ''}
              onChange={(e) => {
                if (e.target.value) {
                  const journey = JOURNEY_TYPES[e.target.value];
                  setCurrentJourney(journey);
                  setJourneyProgress(0);
                  console.log(`🗺️ JOURNEY STARTED: ${journey.title}`);
                } else {
                  setCurrentJourney(null);
                  setJourneyProgress(0);
                  console.log('🗺️ JOURNEY ENDED: Back to free exploration');
                }
              }}
              style={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '0.5rem',
                padding: '0.5rem 1rem',
                backdropFilter: 'blur(8px)',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              <option value="">🗺️ Free Exploration Mode</option>
              {/* <option value="worldCupWinners">🏆 World Cup Champions Journey</option> */}
              {/* <option value="legendaryStadiums">🏟️ Legendary Stadiums Tour</option> */}
            </select>
            
            {/* Journey Progress Indicator */}
            {currentJourney && (
              <div style={{ 
                marginTop: '0.5rem', 
                padding: '0.5rem 1rem',
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: '0.5rem',
                fontSize: '0.8rem',
                color: 'rgba(255,255,255,0.9)'
              }}>
                📍 {currentJourney.description} • Progress: {journeyProgress}/8 countries
              </div>
            )}
          </div>
        )}
      </header>

      {/* API Status */}
      {(isLoadingCountries || apiError) && (
        <div style={{ position: 'relative', zIndex: 30, margin: '0 1.5rem', padding: '1rem', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '0.5rem', marginBottom: '1rem' }}>
          {isLoadingCountries && (
            <div style={{ color: 'white', textAlign: 'center' }}>
              <div style={{ display: 'inline-block', width: '1rem', height: '1rem', border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', marginRight: '0.5rem' }}></div>
              Loading countries from football-data.org...
            </div>
          )}
          
          {apiError && (
            <div style={{ color: '#ff6b6b', textAlign: 'center' }}>
              <strong>⚠️ API Error:</strong> {apiError}
              <br />
              <small>Please check your API keys in .env file</small>
            </div>
          )}
        </div>
      )}

      {/* Main Map Container */}
      <main className="app-main" style={{ position: 'relative', zIndex: 10, flex: 1, padding: '0 1.5rem 1.5rem' }}>
        <div className="premium-map-container hover-lift" style={{ position: 'relative', borderRadius: '1.5rem', overflow: 'visible', margin: '2rem 0' }}>
          
          {/* Map Container */}
          <div
            ref={mapRef}
            className={`stadium-map-canvas${selectedCountry ? ' with-sidebar' : ''}`}
            style={{
              width: selectedCountry ? 'calc(100% - 400px)' : '100%', // Reserve space for sidebar
              height: '520px',
              display: cacheLoaded && !cacheLoadError ? 'block' : 'none',
              pointerEvents: 'auto',
              position: 'relative',
              cursor: 'default',
              overflow: 'visible',
              zIndex: 1,
              transition: 'width 0.3s ease' // Smooth resize animation
            }}
          ></div>

          

          {/* Loading placeholder while countries data loads */}
          {!cacheLoaded && (
            <div className="map-loading-skeleton" style={{ 
              width: '100%', 
              height: '520px', 
              borderRadius: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(34, 139, 34, 0.1)'
            }}>
              <div style={{ textAlign: 'center', color: 'white', width: '280px' }}>
                <div className="loading-pulse" style={{ 
                  width: '3rem', 
                  height: '3rem', 
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  borderRadius: '50%', 
                  margin: '0 auto 1rem' 
                }}></div>
                                
                {/* Real Progress Bar */}
                <div style={{ 
                  width: '100%', 
                  height: '6px', 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  borderRadius: '3px', 
                  marginBottom: '0.75rem',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    height: '100%', 
                    width: `${totalCountries > 0 ? (loadingProgress / totalCountries) * 100 : 0}%`,
                    background: 'linear-gradient(90deg, #22c55e, #16a34a)', 
                    borderRadius: '3px',
                    transition: 'width 0.3s ease-out',
                    boxShadow: '0 0 10px rgba(34, 197, 94, 0.5)'
                  }}></div>
                </div>

                <p style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                  {isLoadingCountries ? `Loading Countries (${loadingProgress}/${totalCountries})` : 'Initializing Map...'}
                </p>
                <p style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                  {isLoadingCountries ? 'Fetching real-time football data...' : 'Setting up interactive map...'}
                </p>
                
                <p style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                  {isLoadingCountries ? '...' : 'Initializing map...'}
                </p>
              </div>
            </div>
          )}
          
          {/* Cache load failure. The venue file is served from our own origin,
              so this is not a proxy or upstream problem - it means
              /stadiums-premium.json did not load. Distinct from apiError,
              which only costs hover and country metadata. */}
          {cacheLoaded && cacheLoadError && (
            <div style={{
              width: '100%',
              height: '520px',
              borderRadius: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(34, 139, 34, 0.1)'
            }}>
              <div style={{ textAlign: 'center', color: 'white', width: '320px' }}>
                <p style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                  Venue data could not be loaded
                </p>
                <p style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                  Reloading the page usually fixes this. If it persists, the
                  problem is on our side.
                </p>
              </div>
            </div>
          )}

          {/* Map Status Overlay */}
          {!isMapLoaded && cacheLoaded && !cacheLoadError && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(34, 139, 34, 0.2)', zIndex: 30 }}>
              <div style={{ textAlign: 'center', color: 'white' }}>
                <div style={{ width: '3rem', height: '3rem', border: '4px solid white', borderTop: '4px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }}></div>
                <p style={{ fontSize: '1.125rem', fontWeight: '500' }}>Loading World Map...</p>
                <p style={{ fontSize: '0.875rem', opacity: 0.8 }}>Connecting to Google Maps API</p>
              </div>
            </div>
          )}

          {/* Loading Overlay for Country */}
          {isLoading && selectedCountry && (
            <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20, borderRadius: '1rem' }}>
              <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center' }}>
                <div style={{ width: '2rem', height: '2rem', border: '3px solid #22c55e', borderTop: '3px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 0.75rem' }}></div>
                <p style={{ fontWeight: '500', color: '#1f2937' }}>Loading {selectedCountry.name}</p>
                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Fetching stadium data...</p>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Mobile-only stadium card: replaces Google's InfoWindow under 768px with our own
          bottom-sheet overlay, since InfoWindow's injected styles can't be reliably controlled. */}
      {mobileStadiumCard && (
        <>
          <div
            className="mobile-stadium-backdrop"
            onClick={() => setMobileStadiumCard(null)}
          />
          <div className="mobile-stadium-panel" key={mobileStadiumCard.id || mobileStadiumCard.name} role="dialog" aria-modal="true">
            <button
              className="mobile-stadium-close-btn"
              onClick={() => setMobileStadiumCard(null)}
              aria-label="Close"
            >
              ×
            </button>
            <div
              className="mobile-stadium-panel-scroll"
              dangerouslySetInnerHTML={{ __html: createProfessionalStadiumPopup(mobileStadiumCard) }}
            />
          </div>
        </>
      )}

      {/* Mobile-only floating toggle: opens the sidebar drawer. Hidden whenever no
          country is selected (nothing to show) or the drawer is already open. */}
      {selectedCountry && !isLoading && !isSidebarDrawerOpen && (
        <button
          className="mobile-drawer-toggle"
          onClick={() => setIsSidebarDrawerOpen(true)}
          aria-label={`Show ${stadiumPins.length} stadiums for ${selectedCountry.name}`}
        >
          🏟️ {stadiumPins.length}
        </button>
      )}

      {/* Mobile-only backdrop: tap outside the drawer to dismiss it */}
      {selectedCountry && !isLoading && isSidebarDrawerOpen && (
        <div
          className="mobile-drawer-backdrop"
          onClick={() => setIsSidebarDrawerOpen(false)}
        />
      )}

      {/* Country Detail Sidebar */}
      {selectedCountry && !isLoading && (
        <aside className={`country-sidebar${isSidebarDrawerOpen ? ' drawer-open' : ''}`} style={{ position: 'fixed', right: 0, top: 0, bottom: 0, width: '24rem', backgroundColor: 'white', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', zIndex: 50, transition: 'transform 0.3s' }}>
          <div className="country-sidebar-inner" style={{ padding: '1.5rem', height: '100%', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
                {selectedCountry.flag && (
                  <img
                    src={selectedCountry.flag}
                    alt={selectedCountry.name}
                    style={{ width: '32px', height: '24px', marginRight: '8px', borderRadius: '2px' }}
                  />
                )}
                {selectedCountry.name}
              </h2>
              {/* Desktop: resets to world view (unchanged, permanent-pane behavior) */}
              <button
                className="sidebar-reset-btn"
                onClick={resetMap}
                style={{ color: '#6b7280', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                ×
              </button>
              {/* Mobile: just closes the drawer, keeping the country/stadium data loaded
                  so the floating toggle re-opens the same view instantly */}
              <button
                className="sidebar-drawer-close-btn"
                onClick={() => setIsSidebarDrawerOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Stadium Overview */}
              <div className="stadium-count-card" style={{ backgroundColor: '#f0fdf4', borderRadius: '0.5rem', padding: '1rem', position: 'relative', overflow: 'hidden' }}>
                <div className="stadium-number" style={{ fontSize: '2.5rem', fontWeight: '900', color: '#16a34a', textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  {stadiumPins.length}
                </div>
                <div style={{ color: '#6b7280', fontSize: '1.1rem', fontWeight: '500' }}>
                  Football Stadiums
                </div>
              </div>
              
              {/* Enhanced League Selector */}
              {availableLeagues.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  {/* The count is availableLeagues only. The dropdown below shows
                      one MORE entry than this number, because "All leagues" is an
                      option rather than a league. */}
                  <h3 style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.75rem' }}>
                    🏆 League Selector ({availableLeagues.length} leagues)
                  </h3>
                  
                  <select 
                    value={selectedLeague || 'all'} 
                    onChange={(e) => {
                      const leagueValue = e.target.value;
                      setSelectedLeague(leagueValue);
                      
                      console.log(`🔄 League dropdown changed to: ${leagueValue}`);
                      
                      if (leagueValue === 'all') {
                        // Show every stadium in the country, across all of its leagues.
                        console.log('🔄 SWITCHING: All leagues view');
                        
                        if (selectedCountry) {
                          // selectedCountry IS the premium file's country key, so it
                          // indexes the cache directly. The old path sent it through
                          // translateCountryNameToCode and then COUNTRY_CODE_TO_NAME,
                          // a round trip that only ever worked by falling through to
                          // the name it started with.
                          const countryData = cachedStadiums?.countries?.[selectedCountry];
                          const leagues = countryData?.leagues || [];
                          
                          // One physical ground can be listed in several leagues - a
                          // stadium hosting both men's and women's football appears once
                          // in each - and every extra listing would drop a second marker
                          // on the same spot. Venue name plus coordinates is the identity
                          // here: teamId belongs to a club, not to a ground.
                          const seen = new Set();
                          const allLeagueStadiums = [];
                          for (const league of leagues) {
                            for (const stadium of league.stadiums || []) {
                              const key = `${stadium.venue}|${stadium.latitude}|${stadium.longitude}`;
                              if (seen.has(key)) continue;
                              seen.add(key);
                              allLeagueStadiums.push({
                                ...stadium,
                                leagueName: league.name,
                                leagueId: league.id,
                                country: selectedCountry
                              });
                            }
                          }
                          
                          if (allLeagueStadiums.length > 0) {
                            const stadiumsWithCoords = allLeagueStadiums.map(stadium => ({
                              // Spread first: this list omitted city, so popups
                              // opened from this path had no travel links.
                              ...stadium,
                              name: stadium.venue,
                              team: stadium.teamName,
                              lat: stadium.latitude,
                              lng: stadium.longitude,
                              coordinates: { lat: stadium.latitude, lng: stadium.longitude },
                              address: stadium.address || '',
                              capacity: stadium.capacity || 0,
                              leagueName: stadium.leagueName,
                              leagueId: stadium.leagueId,
                              id: stadium.teamId,
                              crestUrl: stadium.crestUrl,
                              clubColors: stadium.clubColors,
                              founded: stadium.founded
                            }));
                            
                            clearAllStadiumMarkers();
                            displayStadiumPins(stadiumsWithCoords, googleMapRef.current);
                            setStadiumPins(stadiumsWithCoords);
                            
                            if (window.currentStadiumMarkers.length > 0 && googleMapRef.current) {
                              const bounds = new window.google.maps.LatLngBounds();
                              window.currentStadiumMarkers.forEach(marker => {
                                bounds.extend(marker.getPosition());
                              });
                              googleMapRef.current.fitBounds(bounds);
                              
                              setTimeout(() => {
                                const currentZoom = googleMapRef.current.getZoom();
                                if (currentZoom > 8) {
                                  googleMapRef.current.setZoom(8);
                                }
                              }, 300);
                            }
                            
                            console.log(`✅ Showing ${stadiumsWithCoords.length} stadiums across ${leagues.length} leagues in ${selectedCountry}`);
                            
                            // Standings stay with the country's first league: a table
                            // belongs to one competition, and there is no combined table
                            // to show for "all leagues".
                            const topLeague = leagues[0];
                            if (topLeague) {
                              const leagueStandings = getStandingsFromCache(topLeague.id);
                              if (leagueStandings) {
                                setStandings([{
                                  competition: leagueStandings.competition,
                                  standings: leagueStandings.standings
                                }]);
                              }
                            }
                          }
                        }
                        return;
                      }
                      
                      // Find selected league
                      const league = availableLeagues.find(l => l.id.toString() === leagueValue);
                      if (!league) {
                        console.warn('⚠️ League not found');
                        return;
                      }
                      
                      console.log(`🔄 SWITCHING TO: ${league.name} (ID: ${league.id})`);
                      
                      // ===== NEW: FIND WHICH COUNTRY THIS LEAGUE BELONGS TO =====
                      let leagueCountry = null;
                      let leagueStadiums = [];
                      
                      // Search through ALL countries in cache to find this league
                      if (cachedStadiums && cachedStadiums.countries) {
                        const allCountries = Object.keys(cachedStadiums.countries);
                        
                        for (const countryName of allCountries) {
                          const countryData = cachedStadiums.countries[countryName];
                          
                          // Check if this country has this league
                          const hasLeague = countryData.leagues.some(l => l.id === league.id);
                          
                          if (hasLeague) {
                            leagueCountry = countryName;
                            console.log(`🌍 FOUND: ${league.name} belongs to ${countryName}`);
                            
                            // Get all stadiums for this country
                            const allStadiums = getStadiumsFromCache(countryName);
                            
                            // Filter to only this league
                            leagueStadiums = allStadiums.filter(stadium => 
                              stadium.leagueId === league.id
                            );
                            
                            break; // Found it, stop searching
                          }
                        }
                      }
                      
                      if (!leagueCountry || leagueStadiums.length === 0) {
                        console.warn(`⚠️ No stadiums found for ${league.name}`);
                        alert(`No stadiums found for ${league.name}`);
                        return;
                      }
                      
                      console.log(`⚡ FOUND: ${leagueStadiums.length} stadiums for ${league.name} in ${leagueCountry}`);
                      
                      // Transform cache data to display format
                      const stadiumsWithCoords = leagueStadiums.map(stadium => ({
                        // Spread first: this list omitted city, crestUrl,
                        // clubColors and founded.
                        ...stadium,
                        name: stadium.venue,
                        team: stadium.teamName,
                        lat: stadium.latitude,
                        lng: stadium.longitude,
                        coordinates: { lat: stadium.latitude, lng: stadium.longitude },
                        address: stadium.address || '',
                        capacity: stadium.capacity || 0,
                        leagueName: stadium.leagueName,
                        leagueId: stadium.leagueId,
                        id: stadium.teamId
                      }));
                      
                      // Update selected country if it changed
                      if (leagueCountry !== selectedCountry) {
                        console.log(`🌍 AUTO-SWITCHING: From ${selectedCountry} → ${leagueCountry}`);
                        setSelectedCountry(leagueCountry);
                      }
                      
                      // Clear existing markers
                      if (window.currentStadiumMarkers) {
                        window.currentStadiumMarkers.forEach(marker => marker.setMap(null));
                        window.currentStadiumMarkers = [];
                      }
                      
                      // Display new stadiums
                      displayStadiumPins(stadiumsWithCoords, googleMapRef.current);
                      setStadiumPins(stadiumsWithCoords);
                      
                      // Auto-zoom to fit all stadiums
                      if (window.currentStadiumMarkers.length > 0 && googleMapRef.current) {
                        const bounds = new window.google.maps.LatLngBounds();
                        
                        window.currentStadiumMarkers.forEach(marker => {
                          bounds.extend(marker.getPosition());
                        });
                        
                        googleMapRef.current.fitBounds(bounds);
                        
                        setTimeout(() => {
                          const currentZoom = googleMapRef.current.getZoom();
                          if (currentZoom > 9) {
                            googleMapRef.current.setZoom(9);
                          }
                        }, 300);
                        
                        console.log(`🗺️ Zoomed to fit ${window.currentStadiumMarkers.length} ${league.name} stadiums`);
                      }
                      
                      // Load standings for this league
                      const leagueStandings = getStandingsFromCache(league.id);
                      if (leagueStandings) {
                        setStandings([{
                          competition: leagueStandings.competition,
                          standings: leagueStandings.standings
                        }]);
                        console.log(`⚡ STANDINGS: Loaded for ${league.name}`);
                      }
                      
                      console.log(`✅ COMPLETE: Switched to ${league.name} (${leagueCountry}) - ${stadiumsWithCoords.length} stadiums`);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '0.5rem',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      fontSize: '0.875rem'
                    }}
                  >
                    <option value="all">🌍 All leagues ({allLeaguesVenueCount ?? stadiumPins.length} stadiums)</option>
                    {availableLeagues.map(league => (
                      <option key={league.id} value={league.id}>
                        {league.type === 'League' ? '🏟️' : '🏆'} {league.name}
                      </option>
                    ))}
                  </select>
                  
                  {selectedLeague && selectedLeague !== 'all' && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '0.5rem',
                      fontSize: '0.75rem',
                      color: '#3b82f6'
                    }}>
                      📊 Showing stadiums from selected league only
                    </div>
                  )}
                </div>
              )}

              {/* Stadium List */}
              {stadiumPins.length > 0 && (
                <div>
                  <h3 style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.75rem' }}>
                    🏟️ Stadiums ({stadiumPins.length})
                  </h3>
                  <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {stadiumPins.map((stadium, index) => (
                      <div
                        key={stadium.id}
                        className="stadium-row"
                        style={{
                          backgroundColor: selectedStadium?.id === stadium.id ? '#e0f2fe' : '#f9fafb',
                          borderRadius: '0.5rem',
                          padding: '0.75rem',
                          cursor: 'pointer',
                          border: selectedStadium?.id === stadium.id ? '2px solid #3b82f6' : '1px solid transparent',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => {
                          setSelectedStadium(stadium);
                          if (googleMapRef.current && stadium.coordinates) {
                            googleMapRef.current.panTo(stadium.coordinates);
                            googleMapRef.current.setZoom(15);
                          }
                        }}
                      >
                        <div className="stadium-row-name" style={{ fontWeight: '500', color: '#1f2937', fontSize: '0.9rem' }}>
                          {stadium.name}
                          {stadium.team && (
                            <span style={{ fontWeight: '400', color: '#6b7280', marginLeft: '0.5rem' }}>
                              • {stadium.team}
                            </span>
                          )}
                        </div>
                        <div className="stadium-row-meta" style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>
                          📍 {stadium.city} • 👥 {stadium.capacity?.toLocaleString() || 'Unknown'} capacity
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Loading Stadiums */}
              {isLoadingStadiums && (
                <div style={{ 
                  backgroundColor: '#f0f9ff', 
                  borderRadius: '0.5rem', 
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ 
                    width: '2rem', 
                    height: '2rem', 
                    border: '3px solid #3b82f6', 
                    borderTop: '3px solid transparent', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite', 
                    margin: '0 auto 0.5rem' 
                  }}></div>
                  <p style={{ color: '#3b82f6', fontWeight: '500', margin: 0 }}>
                    Loading stadiums...
                  </p>
                </div>
              )}
              {/* League Standings */}
              {standings && standings.length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                  <h3 style={{ 
                    fontSize: '1.1rem', 
                    fontWeight: '700', 
                    marginBottom: '1rem',
                    color: '#1f2937',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    📊 League Standings
                  </h3>
                  <div style={{ 
                    overflowX: 'auto',
                    backgroundColor: '#fff',
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e7eb'
                  }}>
                    <table style={{ 
                      width: '100%', 
                      fontSize: '0.85rem',
                      borderCollapse: 'collapse'
                    }}>
                      <thead>
                        <tr style={{ 
                          backgroundColor: '#f9fafb',
                          borderBottom: '2px solid #e5e7eb'
                        }}>
                          <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '600' }}>#</th>
                          <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left', fontWeight: '600' }}>Team</th>
                          <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '600' }}>P</th>
                          <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '600' }}>W</th>
                          <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '600' }}>D</th>
                          <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '600' }}>L</th>
                          <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '600', color: '#16a34a' }}>Pts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {standings[0]?.standings?.table?.map((team, idx) => (
                          <tr key={idx} style={{ 
                            borderBottom: '1px solid #f3f4f6',
                            backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa'
                          }}>
                            <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '600', color: '#6b7280' }}>
                              {team.position}
                            </td>
                            <td style={{ padding: '0.75rem 0.5rem', fontWeight: '500' }}>
                              {team.team.name}
                            </td>
                            <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>{team.playedGames}</td>
                            <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>{team.won}</td>
                            <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>{team.draw}</td>
                            <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>{team.lost}</td>
                            <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '700', color: '#16a34a' }}>
                              {team.points}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Block 3 Preview */}
              {/* <div style={{ backgroundColor: '#eff6ff', borderRadius: '0.5rem', padding: '1rem' }}>
                <h3 style={{ fontWeight: '600', color: '#1e40af', marginBottom: '0.5rem' }}>🚀 Block 3 Preview</h3>
                <ul style={{ fontSize: '0.875rem', color: '#1e40af', margin: 0, paddingLeft: '1rem' }}>
                  <li>🔄 Real-time data sync</li>
                  <li>💾 Advanced caching</li>
                  <li>📊 Performance metrics</li>
                  <li>🛡️ Error handling</li>
                </ul>
              </div> */} {/* Hide for now */}
            </div>
          </div>
        </aside>
      )}

      {/* Footer */}
      <footer className="hover-tooltip-footer" style={{
        position: 'fixed',
        bottom: '10px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        padding: '0.5rem 1rem', 
        textAlign: 'center', 
        fontSize: '0.75rem', 
        color: 'rgba(255,255,255,0.9)',
        background: 'rgba(0,0,0,0.7)',
        borderRadius: '1rem',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255,255,255,0.1)',
        maxWidth: '400px'
      }}>
        <div>🌍 Football venues in 135 countries</div>
        <div style={{ marginTop: '0.25rem' }}>🎯 Hover over countries to explore</div>
      </footer>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        /* Premium Football Globe Styles */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        
        .premium-container {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
          min-height: 100vh;
        }
        
        .glassmorphism {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }
        
        .premium-header {
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .premium-map-container {
        background: rgba(15, 23, 42, 0.9);
        backdrop-filter: blur(20px);
        border: 1px solid rgba(34, 197, 94, 0.3);
        box-shadow: 
          0 0 0 1px rgba(34, 197, 94, 0.2),
          0 4px 6px -1px rgba(0, 0, 0, 0.1),
          0 20px 25px -5px rgba(0, 0, 0, 0.2),
          0 10px 10px -5px rgba(0, 0, 0, 0.1),
          inset 0 1px 0 rgba(255, 255, 255, 0.1);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        max-height: 520px; /* ADD: Prevent overflow */
        overflow: hidden;   /* ADD: Hide overflow */
      }

        .premium-map-container:hover {
          transform: translateY(-1px);
          box-shadow: 
            0 0 0 1px rgba(34, 197, 94, 0.3),
            0 6px 8px -1px rgba(0, 0, 0, 0.15),
            0 25px 30px -5px rgba(0, 0, 0, 0.25),
            0 15px 15px -5px rgba(0, 0, 0, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.15);
        }
        
        .hover-lift:hover {
          transform: translateY(-2px);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .football-field-enhanced {
          background: 
            radial-gradient(circle at 50% 50%, rgba(34, 197, 94, 0.15) 0%, transparent 70%),
            linear-gradient(90deg, rgba(255,255,255,0.03) 0%, transparent 50%, rgba(255,255,255,0.03) 100%),
            repeating-linear-gradient(
              90deg,
              transparent,
              transparent 98px,
              rgba(255,255,255,0.02) 100px
            );
          opacity: 0.4;
          animation: fieldPulse 4s ease-in-out infinite;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
          .info-window-enhanced {
            position: relative;
            overflow: hidden;
          }

          .info-window-enhanced::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 2px;
            background: linear-gradient(90deg, #22c55e 0%, #3b82f6 50%, #22c55e 100%);
            background-size: 200% 100%;
            animation: shimmerTop 2s linear infinite;
          }

          @keyframes shimmerTop {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }

        .info-window-animated {
          border-radius: 8px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
          backdrop-filter: blur(10px);
        }

        @keyframes fieldPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.3; }
        }
        
        .pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        .loading-pulse {
          animation: loadingPulse 1.5s ease-in-out infinite;
        }

        @keyframes loadingPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.7;
          }
          50% {
            transform: scale(1.1);
            opacity: 1;
          }
        }

        @keyframes progressMove {
          0% { width: 0%; transform: translateX(-100%); }
          50% { width: 70%; transform: translateX(0%); }
          100% { width: 100%; transform: translateX(100%); }
        }

        .map-loading-skeleton::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255,255,255,0.05) 50%,
            transparent 100%
          );
          animation: shimmer 2s infinite;
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .stadium-count-card {
          transition: all 0.3s ease;
          border: 1px solid rgba(34, 197, 94, 0.2);
        }

        .stadium-count-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(34, 197, 94, 0.15);
        }

        .stadium-number {
          background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .stadium-count-card::before {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 40px;
          height: 40px;
          background: radial-gradient(circle, rgba(34, 197, 94, 0.1) 0%, transparent 70%);
          border-radius: 50%;
        }
        
        .premium-button {
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.8) 0%, rgba(22, 163, 74, 0.9) 100%);
          border: 1px solid rgba(34, 197, 94, 0.5);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }
        
        .premium-button:hover {
          background: linear-gradient(135deg, rgba(34, 197, 94, 1) 0%, rgba(22, 163, 74, 1) 100%);
          box-shadow: 0 0 20px rgba(34, 197, 94, 0.4);
          transform: translateY(-1px);
        }
        
        .stats-badge {
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.3);
          color: #22c55e;
        }

        /* Mobile drawer components - hidden by default, shown only under the mobile media
           query below. Desktop never sees these regardless of the isSidebarDrawerOpen state. */
        .mobile-drawer-toggle {
          display: none;
          position: fixed;
          top: 50%;
          right: 0;
          transform: translateY(-50%);
          align-items: center;
          gap: 6px;
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          color: white;
          border: none;
          padding: 10px 14px 10px 12px;
          border-radius: 999px 0 0 999px;
          font-weight: 700;
          font-size: 0.95rem;
          min-height: 44px;
          box-shadow: -2px 2px 10px rgba(0, 0, 0, 0.25);
          z-index: 190;
          cursor: pointer;
        }

        .mobile-drawer-backdrop {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.45);
          z-index: 200;
        }

        .sidebar-drawer-close-btn {
          display: none;
          background: none;
          border: none;
          color: #6b7280;
          font-size: 1.5rem;
          cursor: pointer;
          min-width: 44px;
          min-height: 44px;
          align-items: center;
          justify-content: center;
        }

        /* Mobile stadium card: our own bottom-sheet overlay, replacing Google's InfoWindow
           under 768px so we control scrolling, the close button, and sizing directly instead
           of fighting Google's injected chrome styles. */
        .mobile-stadium-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.2);
          z-index: 220;
        }

        @keyframes mobileStadiumSlideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }

        .mobile-stadium-panel {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          max-height: 70vh;
          background: #ffffff;
          border-radius: 16px 16px 0 0;
          box-shadow: 0 -8px 30px rgba(0, 0, 0, 0.3);
          overflow: hidden;
          z-index: 230;
          animation: mobileStadiumSlideUp 0.25s ease-out;
        }

        .mobile-stadium-panel-scroll {
          max-height: 70vh;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
          touch-action: pan-y;
        }

        /* The injected card was designed to sit inside Google's InfoWindow bubble - drop its
           own width cap and outer chrome so it fills our panel instead of a card-in-a-card. */
        .mobile-stadium-panel-scroll .stadium-popup-card {
          width: 100% !important;
          border-radius: 0 !important;
          box-shadow: none !important;
        }

        .mobile-stadium-close-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.35);
          color: #ffffff;
          border: none;
          border-radius: 50%;
          font-size: 1.5rem;
          line-height: 1;
          cursor: pointer;
          z-index: 240;
        }

        .info-window-enhanced::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 2px;
            background: linear-gradient(90deg, #22c55e 0%, #3b82f6 50%, #22c55e 100%);
            background-size: 200% 100%;
            animation: shimmerTop 2s linear infinite;
          }

        /* Landscape phones (wide but short): keep the side-by-side layout, but size the map from
           viewport height instead of the fixed 520px so it fills the available space */
        @media (orientation: landscape) and (max-height: 500px) {
          .app-main {
            padding: 0 1rem 0.5rem !important;
          }

          .premium-map-container {
            margin: 0.5rem 0 !important;
            max-height: none !important;
          }

          .stadium-map-canvas {
            height: calc(100vh - 90px) !important;
            /* Let Google Maps' own gesture handling own touch drags instead of the browser
               trying to scroll the page underneath them */
            touch-action: none;
          }

          .stadium-map-canvas.with-sidebar {
            width: 65% !important;
          }

          .country-sidebar {
            width: 35% !important;
          }

          .country-sidebar-inner {
            padding: 0.75rem !important;
            touch-action: pan-y;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
          }

          .country-sidebar-inner h2 {
            font-size: 1.1rem !important;
          }

          .country-sidebar-inner h3 {
            font-size: 0.85rem !important;
            margin-bottom: 0.5rem !important;
          }

          .country-sidebar-inner .stadium-count-card {
            padding: 0.5rem !important;
          }

          .country-sidebar-inner .stadium-number {
            font-size: 1.5rem !important;
          }

          .country-sidebar-inner .stadium-row {
            padding: 0.4rem 0.5rem !important;
          }

          .country-sidebar-inner .stadium-row-name {
            font-size: 0.75rem !important;
          }

          .country-sidebar-inner .stadium-row-meta {
            font-size: 0.65rem !important;
          }

          .country-sidebar-inner table {
            font-size: 0.7rem !important;
          }

          .country-sidebar-inner th,
          .country-sidebar-inner td {
            padding: 0.4rem 0.3rem !important;
          }
        }

        /* Mobile (under 768px wide, either orientation): map fills the full screen below the
           header, and the sidebar becomes a slide-over drawer instead of a permanent pane.
           This is more specific than the landscape rule above for any viewport both narrow
           and short, so it wins the cascade there - the drawer treatment is what we want. */
        @media (max-width: 767px) {
          .app-main {
            padding: 0 !important;
          }

          .premium-map-container {
            margin: 0 !important;
            border-radius: 0 !important;
            max-height: none !important;
          }

          .stadium-map-canvas {
            width: 100% !important;
            height: calc(100vh - 48px) !important;
            /* Let Google Maps' own gesture handling own touch drags instead of the browser
               trying to scroll the page underneath them */
            touch-action: none;
          }

          /* The landscape rule above sets .stadium-map-canvas.with-sidebar to 65% width, and
             that two-class selector outranks the single-class rule above on specificity alone
             (source order doesn't matter once specificity differs) - so it has to be matched
             here explicitly, on any viewport narrow enough to be a drawer instead of a pane. */
          .stadium-map-canvas.with-sidebar {
            width: 100% !important;
          }

          .mobile-drawer-toggle {
            display: flex !important;
          }

          .mobile-drawer-backdrop {
            display: block !important;
          }

          .country-sidebar {
            position: fixed !important;
            top: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 85% !important;
            max-width: 85% !important;
            transform: translateX(100%);
            z-index: 210 !important;
          }

          .country-sidebar.drawer-open {
            transform: translateX(0);
          }

          .country-sidebar-inner {
            touch-action: pan-y;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
          }

          .sidebar-reset-btn {
            display: none !important;
          }

          .sidebar-drawer-close-btn {
            display: inline-flex !important;
          }

          .stadium-marker-legend {
            display: none !important;
          }
        }

        /* Compact header: under 768px wide OR under 500px tall (covers both portrait and
           landscape phones) - collapse to a single ~44px bar with just the logo and title */
        @media (max-width: 767px), (max-height: 499px) {
          .premium-header {
            padding: 0.5rem 1rem !important;
          }

          .header-logo {
            width: 28px !important;
            height: 28px !important;
          }

          .header-wordmark-kicker {
            font-size: 0.625rem !important;
          }

          .header-wordmark {
            font-size: 1.1rem !important;
          }

          .header-tagline,
          .header-badges,
          .header-journey {
            display: none !important;
          }

          /* References hovering, which doesn't exist on touch */
          .hover-tooltip-footer {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default FootballGlobe;